/**
 * ProDebtCo Unified Lead Tracker System
 * 
 * Combines the best of both tracking systems:
 * - FormFieldLogger: Real-time interaction tracking, session management, event batching
 * - ProDebtCoLeadTracker: Simple webhook processing, clean session generation
 * 
 * Eliminates dual-system conflicts while preserving all critical functionality
 */

import config, { type UnifiedConfig, getApiHeaders } from './config/unified'
import { SessionManager, type SessionData, type SessionMetadata } from './session/SessionManager'
import { initializeFormatter } from './formatting'
import { initAddressAutocomplete } from './address'
import { componentRendererRegistry, type FormCompletionData, hideAllOutcomeComponents } from './component-renderer'
import { initializeComponentSystem, getComponentRenderer } from './component-renderer/system'
import type { CompletionState } from './component-renderer/core/component-renderer'
import { getGclid, getGcid, getFbp, getFbc, getFbCampaignId, getFbAdsetId, getFbAdId, getFbAdgroup, getFbCreative, cleanSessionId } from './utils/tracking-helpers'
import { logger, LogCategory, initializeDataLayer, trackFormStart, trackFormSubmission, trackCreditDecision, trackFBViewContent, trackFBContact, trackFBLead } from './logging'

// Pre-fill imports
import { isGetMyLowRatesPage, getRefIdFromUrl } from './utils/url-detector'
import { fetchLeadByRefId } from './api/lead-data-fetcher'
import { mapExternalLeadToFormData, getStepsToHide, validatePreFilledData } from './form-prefill/form-prefill-manager'
import { MultiStepController } from './multi-step/multi-step-controller'

// Additional-information capture (CD scenarios 5 & 7)
import { initAdditionalInfoForm } from './additional-info-form'

// Type definitions
interface FormInteractionEvent {
  session_id: string
  field_name: string
  field_type: string
  event_type: 'focus' | 'input' | 'blur' | 'change'
  field_value?: string
  form_step?: number
  timestamp: string
  sequence_number?: number
  time_on_field?: number
  keystroke_count?: number
  paste_detected?: boolean
  validation_errors?: Record<string, string>
  page_url: string
  metadata?: Record<string, any>
}

interface LeadData {
  session_id: string
  full_name?: string
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  loan_amount?: number
  annual_income?: number
  employment_status?: string
  address_street?: string
  address_city?: string
  address_state?: string
  address_zip?: string
  housing_status?: string
  monthly_housing_payment?: number
  ssn?: string
  date_of_birth?: string
  additional_info?: Record<string, any>
}

interface FormFieldData {
  fieldName: string
  fieldType: string
  value: string
  previousValue?: string
  eventType: string
  timestamp: string
  sessionId: string
  stepNumber: number
  timeOnField?: number
  keystrokeCount?: number
  pasteDetected?: boolean
  validationErrors?: Record<string, string>
  environment: string
  pageUrl: string
  formId?: string
}

// Event batching system for performance
class EventBatcher {
  private batches: Map<string, FormInteractionEvent[]> = new Map()
  private timers: Map<string, NodeJS.Timeout> = new Map()
  private readonly batchSize: number
  private readonly flushInterval: number

  constructor(batchSize: number = 50, flushInterval: number = 5000) {
    this.batchSize = batchSize
    this.flushInterval = flushInterval
  }

  addEvent(event: FormInteractionEvent): void {
    const sessionId = event.session_id
    
    if (!this.batches.has(sessionId)) {
      this.batches.set(sessionId, [])
    }
    
    const batch = this.batches.get(sessionId)!
    batch.push(event)
    
    // Flush batch if it reaches the size limit
    if (batch.length >= this.batchSize) {
      this.flushBatch(sessionId)
      return
    }
    
    // Set or reset the flush timer
    if (this.timers.has(sessionId)) {
      clearTimeout(this.timers.get(sessionId)!)
    }
    
    const timer = setTimeout(() => {
      this.flushBatch(sessionId)
    }, this.flushInterval)
    
    this.timers.set(sessionId, timer)
  }
  
  private async flushBatch(sessionId: string): Promise<void> {
    const batch = this.batches.get(sessionId)
    if (!batch || batch.length === 0) return
    
    // Clear the batch and timer
    this.batches.delete(sessionId)
    if (this.timers.has(sessionId)) {
      clearTimeout(this.timers.get(sessionId)!)
      this.timers.delete(sessionId)
    }
    
    try {
      // Send batch to Supabase edge function
      const response = await fetch(config.edgeFunctions.endpoints.processFormInteraction, {
        method: 'POST',
        headers: getApiHeaders(config),
        body: JSON.stringify({
          events: batch.map(event => ({
            ...event,
            session_id: event.session_id.replace(/^sess_/, '') // Clean session ID for DB
          })),
          batch_id: `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      if (config.debug) {
        console.log(`📤 Flushed ${batch.length} events for session: ${sessionId.replace('sess_', '').substring(0, 8)}`)
      }
    } catch (error) {
      console.error('Failed to flush event batch for session:', sessionId, error)
      // Could implement retry logic here if needed
    }
  }
  
  // Force flush all batches (useful for page unload)
  async flushAll(): Promise<void> {
    const promises: Promise<void>[] = []
    
    for (const sessionId of this.batches.keys()) {
      promises.push(this.flushBatch(sessionId))
    }
    
    await Promise.all(promises)
  }
  
  // Get pending event count for a session
  getPendingCount(sessionId: string): number {
    return this.batches.get(sessionId)?.length || 0
  }
}

// Main Unified Lead Tracker Class
export class UnifiedLeadTracker {
  // Core state
  private sessionManager: SessionManager
  private eventBatcher: EventBatcher
  private fieldData: FormFieldData[] = []
  private stepNumber: number = 1
  private sequenceNumber: number = 1
  private isOnline: boolean = navigator.onLine
  private supabaseInitialized: boolean = false

  // Field tracking
  private fieldStartTimes: Map<string, number> = new Map()
  private fieldKeystrokeCounts: Map<string, number> = new Map()
  private boundFields: Set<string> = new Set()
  private boundButtons: Set<Element> = new Set()

  // Lead processing state - MUST BE RESET PER SESSION
  private hasInitialWebhook: boolean = false
  private hasPartialCreatedWebhook: boolean = false
  private lastUpdateTime: number = 0
  private isFormCompleted: boolean = false
  private formSubmitted: boolean = false
  private lastSubmissionTime: number = 0  // Prevent rapid-fire submissions

  // Facebook Pixel event tracking (CreditDirect only)
  private hasFiredViewContent: boolean = false
  private hasFiredContactEvent: boolean = false
  private hasFiredLeadEvent: boolean = false

  // Track current session ID to detect changes
  private currentSessionId: string = ''
  
  // Request serialization to prevent race conditions
  private processingQueue: Promise<any> = Promise.resolve()
  private isProcessing: boolean = false
  private processingDebounceTimer: NodeJS.Timeout | null = null

  // Pending request tracking for cancellation
  private pendingRequests: Set<AbortController> = new Set()

  // Refresh prevention (back button education)
  private refreshWarningElement: HTMLElement | null = null
  private static readonly FORM_LOCKIN_KEY = 'form_refresh_lock'
  private static readonly FORM_LOCKIN_DURATION_MS = 30 * 60 * 1000 // 30 minutes

  // Pre-fill state (for application-form-get-my-low-rates page)
  private prefilledData: Record<string, any> | null = null
  private refId: string | null = null

  // Configuration
  private readonly config: UnifiedConfig

  constructor(customConfig?: Partial<UnifiedConfig>) {
    // VERY FIRST LINE - Set debug flag
    try {
      if (typeof window !== 'undefined') {
        (window as any).__PREFILL_DEBUG_CONSTRUCTOR_START = new Date().toISOString()
      }
    } catch (e) {
      // Ignore
    }

    this.config = { ...config, ...customConfig }

    // Initialize core systems
    this.sessionManager = new SessionManager()
    this.eventBatcher = new EventBatcher(
      this.config.eventProcessing.batchSize,
      this.config.eventProcessing.flushInterval
    )

    // Setup event listeners
    this.setupOnlineOfflineListeners()
    this.setupPageUnloadListeners()
    this.setupRefreshPrevention()
    this.preventMobileInputZoom()

    // Store initial session ID
    this.currentSessionId = this.sessionManager.getSessionId()

    // CRITICAL: Reset ALL state for fresh session
    this.resetAllProcessingState()

    // Initialize pre-fill if on Get My Low Rates page (async, non-blocking)
    ;(window as any).__PREFILL_DEBUG = {
      constructorCalled: true,
      timestamp: new Date().toISOString()
    }

    console.log('🚀 CONSTRUCTOR: About to call initializePreFill()')
    console.warn('🚀 CONSTRUCTOR: Using console.warn to ensure visibility')
    console.error('🚀 CONSTRUCTOR: Using console.error to ensure visibility')

    try {
      const prefillPromise = this.initializePreFill()
      console.log('🚀 CONSTRUCTOR: initializePreFill() returned a promise:', prefillPromise)

      prefillPromise.then(() => {
        console.log('✅ CONSTRUCTOR: initializePreFill() completed successfully')
        ;(window as any).__PREFILL_DEBUG.completed = true
      }).catch(error => {
        console.error('❌ CONSTRUCTOR: Pre-fill initialization failed:', error)
        console.error('   Error stack:', error.stack)
        ;(window as any).__PREFILL_DEBUG.error = error.message
        // Graceful degradation - form still works without pre-fill
      })
      console.log('🚀 CONSTRUCTOR: initializePreFill() promise setup complete')
    } catch (syncError: any) {
      console.error('❌ CONSTRUCTOR: Synchronous error calling initializePreFill:', syncError)
      ;(window as any).__PREFILL_DEBUG.syncError = syncError?.message || String(syncError)
    }
  }

  // CRITICAL: Reset ALL state when session changes
  private checkAndHandleSessionChange(): void {
    const currentSession = this.sessionManager.getSessionId()
    if (currentSession !== this.currentSessionId) {
      if (this.config.debug) {
        console.log('🔄 SESSION CHANGE DETECTED! Resetting ALL state for new session')
        console.log(`   Old session: ${this.currentSessionId}`)
        console.log(`   New session: ${currentSession}`)
      }

      // Update session ID
      this.currentSessionId = currentSession

      // CRITICAL: Don't reset state if we have a valid completion state
      // Completion state persists across sessions (24h window, same browser)
      const renderer = getComponentRenderer()
      const hasSavedCompletionState = renderer?.checkForSavedState()

      if (hasSavedCompletionState) {
        if (this.config.debug) {
          console.log('✨ Completion state found - preserving across session change')
          console.log('   Session changed but user already completed form within 24h')
        }
        // Don't reset state - user already completed form
        return
      }

      // RESET EVERYTHING for the new session (only if no completion state)
      this.resetAllProcessingState()
    }
  }
  
  // Reset ALL processing state for new sessions
  private resetAllProcessingState(): void {
    if (this.config.debug) {
      console.log('🧹 Resetting ALL tracker state for clean session')
    }
    
    // Reset ALL lead processing flags
    this.hasInitialWebhook = false
    this.hasPartialCreatedWebhook = false
    this.lastUpdateTime = 0
    this.isFormCompleted = false
    this.formSubmitted = false
    this.lastSubmissionTime = 0

    // Reset Facebook Pixel event flags
    this.hasFiredViewContent = false
    this.hasFiredContactEvent = false
    this.hasFiredLeadEvent = false

    // Reset processing state
    this.isProcessing = false
    this.processingQueue = Promise.resolve()
    if (this.processingDebounceTimer) {
      clearTimeout(this.processingDebounceTimer)
      this.processingDebounceTimer = null
    }
    
    // Reset field tracking
    this.fieldData = []
    this.stepNumber = 1
    this.sequenceNumber = 1
    this.fieldStartTimes.clear()
    this.fieldKeystrokeCounts.clear()
    
    // Clear comparison data
    this.lastSentData = undefined
    this.lastSentFieldCount = undefined
    
    // Don't reset: boundFields, boundButtons (these track DOM bindings)
    // Don't reset: sessionManager, eventBatcher (core systems)
    // Don't reset: config, isOnline, supabaseInitialized (environment state)
  }

  /**
   * Initialize form pre-fill for Get My Low Rates page
   * Fetches lead data from external API and applies to form
   * Triggers partial_created webhook immediately after pre-fill
   */
  private async initializePreFill(): Promise<void> {
    console.log('🚀🚀🚀 PRE-FILL INIT START (VERY FIRST LINE)')
    console.log('   window.location.href:', window.location.href)
    console.log('   window.location.pathname:', window.location.pathname)
    console.log('   window.location.search:', window.location.search)

    // ALWAYS log - even if not on the right page
    console.log('🚀 PRE-FILL INIT CHECK:', {
      currentUrl: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      isGetMyLowRatesPage: isGetMyLowRatesPage(),
      refId: getRefIdFromUrl()
    })

    // Check if we're on the Get My Low Rates page
    if (!isGetMyLowRatesPage()) {
      console.log('❌ Not on Get My Low Rates page - skipping pre-fill')
      return // Not on pre-fill page
    }

    // Extract refId from URL
    const refId = getRefIdFromUrl()
    if (!refId) {
      console.log('ℹ️ Get My Low Rates page detected but no refId parameter - showing empty form')
      // Defer so Webflow's own form JS finishes running before we take over navigation
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const controller = new MultiStepController(this.config.debug)
        controller.initialize([])
      }))
      return
    }

    console.log(`🔍 Initializing pre-fill for refId: ${refId}`)

    try {
      // Fetch lead data from external API
      const leadData = await fetchLeadByRefId(
        refId,
        this.config.externalApi.baseUrl,
        this.config.externalApi.headers['x-api-auth']
      )

      if (!leadData) {
        console.log('⚠️ Failed to fetch lead data - showing empty form (graceful degradation)')
        return
      }

      // Map API data to form field names
      const formData = mapExternalLeadToFormData(leadData)

      // Validate pre-filled data
      if (!validatePreFilledData(formData)) {
        console.warn('⚠️ Pre-filled data validation failed - showing empty form')
        return
      }

      // Store pre-filled data and refId
      this.prefilledData = formData
      this.refId = refId

      console.log(`✅ Lead data fetched and mapped:`, {
        fieldsCount: Object.keys(formData).length,
        refId
      })

      // Determine which steps to hide
      const stepsToHide = getStepsToHide(formData)

      // Apply pre-fill to form (wait for DOM to be ready)
      this.applyPreFillToDOM(formData, stepsToHide)

      // CRITICAL: Trigger partial_created webhook immediately with pre-filled data
      console.log('🎯 Triggering partial_created webhook with pre-filled data...')
      await this.processLead(false, true) // is_first_qualified = true
      this.hasPartialCreatedWebhook = true
      this.setFormLockIn() // Lock in user - prevent accidental refresh

      console.log('✅ Pre-fill complete - partial_created webhook triggered')

    } catch (error) {
      console.error('❌ Pre-fill initialization error:', error)
      // Graceful degradation - form still works without pre-fill
    }
  }

  /**
   * Apply pre-filled data to DOM elements
   * @param formData - Pre-filled form data
   * @param stepsToHide - Array of step numbers to hide
   */
  private applyPreFillToDOM(formData: Record<string, any>, stepsToHide: number[]): void {
    // Wait for DOM to be ready (form may not be rendered yet)
    const applyPreFill = () => {
      const renderer = getComponentRenderer()
      if (renderer) {
        renderer.applyPreFillToForm(formData, stepsToHide)
      } else {
        console.warn('⚠️ Component renderer not available - retrying in 100ms')
        setTimeout(applyPreFill, 100)
      }
    }

    // Apply pre-fill after a short delay to ensure DOM is ready
    setTimeout(applyPreFill, 500)
  }

  /**
   * Cancel all pending API requests
   * Called when credit decision is received to prevent race conditions
   */
  private cancelAllPendingRequests(): void {
    if (this.pendingRequests.size === 0) {
      if (this.config.debug) {
        console.log('✅ No pending requests to cancel')
      }
      return
    }

    if (this.config.debug) {
      console.log(`🛑 Cancelling ${this.pendingRequests.size} pending API request(s)`)
    }

    // Abort all pending requests
    this.pendingRequests.forEach(controller => {
      try {
        controller.abort()
      } catch (error) {
        // Ignore abort errors - request already completed
      }
    })

    // Clear the set
    this.pendingRequests.clear()

    if (this.config.debug) {
      console.log('✅ All pending requests cancelled')
    }
  }

  /**
   * Hash a value using SHA-256 according to Facebook's standards
   * @param value - Value to hash (email, phone, name, etc.)
   * @returns SHA-256 hash as hexadecimal string
   */
  private async hashSHA256(value: string): Promise<string> {
    if (!value) return ''

    // Normalize: lowercase and trim whitespace per Facebook standards
    const normalized = value.toLowerCase().trim()

    // Hash using SubtleCrypto API
    const encoder = new TextEncoder()
    const data = encoder.encode(normalized)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

    return hashHex
  }

  /**
   * Fire a Facebook Pixel event (CreditDirect only)
   * @param eventName - Facebook Pixel event name (ViewContent, Contact, Lead, etc.)
   * @param params - Event parameters
   */
  private fireFacebookPixelEvent(eventName: string, params: Record<string, any>): void {
    const siteConfig = (window as any).__SITE_CONFIG__

    // CreditDirect only - no events for ACCS
    if (siteConfig?.brand !== 'creditdirect') {
      return
    }

    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', eventName, params)
      if (this.config.debug) {
        console.log(`📘 Facebook Pixel: ${eventName}`, params)
      }
    } else if (this.config.debug) {
      console.warn('⚠️ Facebook Pixel (fbq) not loaded - skipping event:', eventName)
    }
  }

  // Initialize the unified tracker
  async init(): Promise<void> {
    if (this.config.debug) {
      console.log('🚀 Initializing Unified Lead Tracker...')
    }

    // CRITICAL: Check for session changes first
    this.checkAndHandleSessionChange()

    // Initialize logger with session ID
    logger.setSessionId(this.sessionManager.getSessionId())
    logger.info(LogCategory.API, 'Unified Lead Tracker initializing', {
      session_id: this.sessionManager.getSessionId(),
      environment: this.config.environment,
      debug: this.config.debug
    })

    // Initialize GTM dataLayer
    initializeDataLayer()

    try {
      // Initialize Supabase session
      await this.initializeSupabaseSession()

      // CRITICAL: Check for saved completion state (24h persistence)
      // If user already completed form, restore their outcome component instead of showing form again
      const savedCompletionState = await this.checkForSavedCompletionState()
      if (savedCompletionState) {
        if (this.config.debug) {
          console.log('✨ Saved completion state found - restoring component instead of form')
        }
        // Skip form binding, component renderer will handle everything
        return
      }

      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.bindFormFields())
      } else {
        this.bindFormFields()
      }

      // Try again after delays to catch dynamically loaded content (Webflow)
      // Use flags to prevent duplicate bindings
      setTimeout(() => {
        if (this.config.debug) {
          console.log('🔄 Re-binding form fields (1s delay for dynamic content)')
        }
        this.bindFormFields()
      }, 1000)
      setTimeout(() => {
        if (this.config.debug) {
          console.log('🔄 Re-binding form fields (3s delay for dynamic content)')
        }
        this.bindFormFields()
      }, 3000)

      // On-page debug overlay (Session / Env / Online / Supabase / Pending)
      // intentionally disabled — it's distracting on dev and we don't want it
      // showing while validating against the live site. Console logging stays.
      // To re-enable, restore: if (this.config.debug) this.addVisualIndicator()

      // Initialize additional-information form module (CD only).
      // No-op for ACCS and no-op when the host element isn't present.
      const siteConfigForAdditional = typeof window !== 'undefined' && (window as any).__SITE_CONFIG__
      if (siteConfigForAdditional?.brand === 'creditdirect') {
        initAdditionalInfoForm({
          // dcapi keys the additional-info submit on external_transaction_id —
          // the SAME value the credit-pull request sent: cleanSessionId(sessionId)
          // (the sess_-stripped session id). Must match exactly or dcapi returns
          // "Lead not found for session_id". No refId here: the credit-pull
          // request never sends refId, and the pre-fill (?refid=) flow never
          // reaches O5/O7 anyway.
          sessionId: cleanSessionId(this.sessionManager.getSessionId()),
          partialSaveUrl: this.config.edgeFunctions.endpoints.simpleLeadProcessor,
          submitUrl: this.config.additionalInformationUrl,
          supabaseAnonKey: this.config.supabase.anonKey,
          debug: this.config.debug,
          trackingData: { brand: 'creditdirect' },
          // Carry the loan amount + home-ownership the applicant already gave
          // (before the credit pull) into the additional-info submit, so SGN
          // gets loan_amount + home_owner without re-prompting. Resolved lazily
          // at submit time — these aren't known when this module initializes.
          getCarryForwardFields: () => this.getAdditionalInfoCarryForward(),
        })
        if (this.config.debug) {
          console.log('🧾 Additional-information form module initialized (CD)')
        }

        // Show the decline page ONLY when the additional-info submit comes back
        // with an explicit dcapi decline (status:"decline", scenario 7). Accept
        // is handled in-controller (redirect); errors show the in-form banner.
        // We deliberately gate on outcome.declined, not just "not accepted".
        window.addEventListener('additional-info:non-accept-response', (evt) => {
          const outcome = (evt as CustomEvent)?.detail?.outcome
          if (!outcome?.declined) return
          const renderer = getComponentRenderer()
          if (renderer) {
            renderer
              .showAdditionalInfoDecline(this.sessionManager.getSessionId())
              .catch((e) => console.warn('[additional-info] decline render failed:', e))
          }
        })
      }

      // Setup periodic sync for better reliability
      setInterval(() => {
        if (this.isOnline && this.supabaseInitialized) {
          this.eventBatcher.flushAll().catch(console.warn)
        }
      }, 30000) // Sync every 30 seconds

      if (this.config.debug) {
        console.log('✅ Unified Lead Tracker initialized successfully')
        console.log(`📱 Session: ${this.sessionManager.getSessionId()}`)
      }

    } catch (error) {
      console.error('❌ Failed to initialize Unified Lead Tracker:', error)
    }
  }

  // Initialize session in Supabase
  private async initializeSupabaseSession(): Promise<void> {
    // Check for session changes before initializing
    this.checkAndHandleSessionChange()
    
    // Prevent duplicate session creation
    if (this.supabaseInitialized) {
      if (this.config.debug) {
        console.log('⚠️ Supabase session already initialized - skipping duplicate')
      }
      return
    }
    
    try {
      const metadata = this.sessionManager.getMetadata()
      const browserInfo = this.getBrowserInfo()

      // Get brand from site config (set by entry point before tracker loads)
      const siteConfig = typeof window !== 'undefined' && (window as any).__SITE_CONFIG__
      const brand = siteConfig?.brand || 'unknown'

      const enrichedMetadata: SessionMetadata = {
        ...metadata,
        ...this.extractAllQueryParameters(),
        // CRITICAL: Pass brand to edge function so it's set in database
        brand: brand,
        // Ensure required string fields have defaults
        userAgent: browserInfo.user_agent || navigator.userAgent || 'unknown',
        language: browserInfo.language || navigator.language || 'en-US',
        screenResolution: browserInfo.screen_resolution || `${screen.width}x${screen.height}`,
        viewportSize: browserInfo.viewport_size || `${window.innerWidth}x${window.innerHeight}`,
        deviceType: (browserInfo.device_type as 'desktop' | 'tablet' | 'mobile' | 'unknown') || 'unknown',
        touchCapable: 'ontouchstart' in window,
        referrer: document.referrer || '',
        landingPage: window.location.href,
        timezone: browserInfo.timezone || 'UTC',
        sessionStartTime: Date.now(),
        pageLoadTime: performance.timing ? performance.timing.loadEventEnd - performance.timing.navigationStart : undefined
      }
      
      const sessionResult = await this.createSession(
        this.sessionManager.getSessionId(),
        enrichedMetadata
      )
      
      if (sessionResult.success) {
        this.supabaseInitialized = true
        if (this.config.debug) {
          const action = this.sessionManager.wasRestored() ? 'validated' : 'created'
          console.log(`✅ Supabase session ${action}:`, sessionResult.data?.session_id)
        }
      } else {
        console.warn('⚠️ Supabase session creation failed:', sessionResult.error)
        // If it failed because session already exists, that's actually OK
        if (sessionResult.error?.includes('already exists')) {
          this.supabaseInitialized = true
        }
      }
    } catch (error) {
      console.warn('⚠️ Could not create Supabase session (offline?):', error)
    }
  }

  // Create session via edge function
  private async createSession(sessionId?: string, metadata?: SessionMetadata): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const cleanedSessionId = sessionId?.replace(/^sess_/, '')

      logger.debug(LogCategory.API, 'Creating Supabase session', {
        session_id: cleanedSessionId,
        brand: (metadata as any)?.brand
      })

      const response = await fetch(config.edgeFunctions.endpoints.createSession, {
        method: 'POST',
        headers: getApiHeaders(config),
        body: JSON.stringify({
          session_id: cleanedSessionId,
          metadata: metadata || {}
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      logger.info(LogCategory.API, 'Session created successfully', {
        session_id: cleanedSessionId,
        success: result.success
      })

      return result
    } catch (error) {
      logger.error(LogCategory.API, 'Session creation failed', {
        session_id: sessionId,
        error: (error as Error).message
      })
      return {
        success: false,
        error: (error as Error).message
      }
    }
  }

  // Setup online/offline listeners
  private setupOnlineOfflineListeners(): void {
    window.addEventListener('online', () => {
      this.isOnline = true
      if (this.config.debug) {
        console.log('🌐 Connection restored - syncing pending interactions')
      }
      this.eventBatcher.flushAll()
      
      // Retry Supabase initialization if not done
      if (!this.supabaseInitialized) {
        this.initializeSupabaseSession()
      }
    })

    window.addEventListener('offline', () => {
      this.isOnline = false
      if (this.config.debug) {
        console.log('📵 Connection lost - interactions will be queued')
      }
    })
  }

  // Setup page unload listeners
  private setupPageUnloadListeners(): void {
    const handleUnload = () => {
      // Just flush pending events, don't mark as complete
      // Sessions are abandoned, not completed when user leaves
      this.eventBatcher.flushAll()

      // Renew session for next page load
      this.sessionManager.renewSession()

      // DO NOT call processLead(true) here - that marks it as complete
      // which triggers credit_decision webhooks incorrectly
      if (this.config.debug) {
        console.log('🚪 Page unloading - session will be abandoned (not completed)')
      }
    }

    // beforeunload with form lock-in check (shows browser native dialog if locked in)
    window.addEventListener('beforeunload', (e) => {
      handleUnload()

      // Show browser's native "Leave site?" dialog if form is locked in
      if (this.isFormLockedIn()) {
        e.preventDefault()
        e.returnValue = '' // Required for Chrome
        return ''
      }
    })

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        handleUnload()
      }
    })
    window.addEventListener('pagehide', handleUnload)
  }

  // ==================== REFRESH PREVENTION (Back Button Education) ====================

  // Set form lock-in state (called after partial_created webhook succeeds)
  private setFormLockIn(): void {
    const state = {
      sessionId: this.sessionManager.getSessionId(),
      lockedAt: Date.now()
    }
    localStorage.setItem(UnifiedLeadTracker.FORM_LOCKIN_KEY, JSON.stringify(state))
    // Apply pull-to-refresh CSS prevention
    if ((this as any).updatePullToRefreshCSS) {
      (this as any).updatePullToRefreshCSS()
    }
    if (this.config.debug) {
      console.log('🔒 Form lock-in activated (30 min expiry)')
    }
  }

  // Check if form is currently locked in
  private isFormLockedIn(): boolean {
    const stored = localStorage.getItem(UnifiedLeadTracker.FORM_LOCKIN_KEY)
    if (!stored) return false

    try {
      const state = JSON.parse(stored)
      const elapsed = Date.now() - state.lockedAt

      // Expired after 30 minutes
      if (elapsed > UnifiedLeadTracker.FORM_LOCKIN_DURATION_MS) {
        localStorage.removeItem(UnifiedLeadTracker.FORM_LOCKIN_KEY)
        if (this.config.debug) {
          console.log('🔓 Form lock-in expired')
        }
        return false
      }

      return true
    } catch {
      localStorage.removeItem(UnifiedLeadTracker.FORM_LOCKIN_KEY)
      return false
    }
  }

  // Clear form lock-in state (called on form completion)
  private clearFormLockIn(): void {
    localStorage.removeItem(UnifiedLeadTracker.FORM_LOCKIN_KEY)
    // Remove pull-to-refresh CSS prevention
    if ((this as any).updatePullToRefreshCSS) {
      (this as any).updatePullToRefreshCSS()
    }
    if (this.config.debug) {
      console.log('🔓 Form lock-in cleared (form completed)')
    }
  }

  // Setup keyboard refresh interception (F5, Cmd+R, Ctrl+R) and mobile pull-to-refresh
  private setupRefreshPrevention(): void {
    // Desktop: Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      if (!this.isFormLockedIn()) return

      // F5 or Cmd/Ctrl + R
      const isRefreshKey = e.key === 'F5' ||
        ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'r')

      if (isRefreshKey) {
        e.preventDefault()
        e.stopPropagation()
        this.showRefreshWarningMessage()
        if (this.config.debug) {
          console.log('⚠️ Refresh key blocked - showing warning message')
        }
      }
    })

    // Mobile: Pull-to-refresh prevention
    let touchStartY = 0
    let isPullingDown = false

    document.addEventListener('touchstart', (e) => {
      if (!this.isFormLockedIn()) return
      touchStartY = e.touches[0].clientY
    }, { passive: true })

    document.addEventListener('touchmove', (e) => {
      if (!this.isFormLockedIn()) return

      const touchY = e.touches[0].clientY
      const scrollTop = window.scrollY || document.documentElement.scrollTop

      // Detect pull-to-refresh: at top of page + pulling down
      if (scrollTop <= 0 && touchY > touchStartY + 50) {
        if (!isPullingDown) {
          isPullingDown = true
          this.showRefreshWarningMessage()
          if (this.config.debug) {
            console.log('⚠️ Pull-to-refresh detected - showing warning message')
          }
        }
      }
    }, { passive: true })

    document.addEventListener('touchend', () => {
      isPullingDown = false
    }, { passive: true })

    // Apply CSS to prevent pull-to-refresh when locked in (checked periodically)
    this.setupPullToRefreshCSS()
  }

  // Prevent mobile auto-zoom when tapping form inputs.
  //
  // Two separate root causes, two separate fixes:
  //
  // iOS Safari: zooms based on viewport scale when input is focused.
  //   Fix: temporarily inject maximum-scale=1 into the viewport meta on focus,
  //   restore on blur so intentional pinch-to-zoom still works elsewhere.
  //
  // Android Chrome / WebView (incl. Facebook in-app browser on Android 14/15):
  //   zooms when input font-size < 16px — ignores viewport meta changes entirely.
  //   Fix: inject a <style> rule setting font-size:16px on focused inputs.
  //   touch-action:manipulation suppresses double-tap zoom and helps Android's
  //   WebView skip the zoom heuristic altogether.
  //   Only injected on touch-capable devices to avoid affecting desktop layouts.
  private preventMobileInputZoom(): void {
    // ── Strategy 1: viewport maximum-scale=1 on focus/blur (iOS Safari) ──────
    const viewport = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null
    if (viewport) {
      const base = viewport.getAttribute('content') || ''
      // Strip any pre-existing maximum-scale so we don't double-add it
      const baseClean = base.replace(/,?\s*maximum-scale=[^,]*/g, '').trim()

      document.addEventListener('focus', (e) => {
        const tag = (e.target as Element)?.tagName
        if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') {
          viewport.setAttribute('content', `${baseClean}, maximum-scale=1`)
        }
      }, true)

      document.addEventListener('blur', (e) => {
        const tag = (e.target as Element)?.tagName
        if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') {
          viewport.setAttribute('content', baseClean)
        }
      }, true)
    }

    // ── Strategy 2: font-size 16px on focus + touch-action (Android) ─────────
    if (('ontouchstart' in window || navigator.maxTouchPoints > 0) &&
        !document.getElementById('no-input-zoom')) {
      const style = document.createElement('style')
      style.id = 'no-input-zoom'
      style.textContent =
        'input,select,textarea{touch-action:manipulation}' +
        'input:focus,select:focus,textarea:focus{font-size:16px!important}'
      document.head.appendChild(style)
    }
  }

  // Apply/remove CSS to prevent pull-to-refresh based on lock-in state
  private setupPullToRefreshCSS(): void {
    const styleId = 'form-lockin-style'

    // Check lock-in state and apply CSS accordingly
    const updateCSS = () => {
      let styleEl = document.getElementById(styleId) as HTMLStyleElement | null

      if (this.isFormLockedIn()) {
        if (!styleEl) {
          styleEl = document.createElement('style')
          styleEl.id = styleId
          styleEl.textContent = `
            html, body {
              overscroll-behavior-y: contain !important;
            }
          `
          document.head.appendChild(styleEl)
        }
      } else {
        if (styleEl) {
          styleEl.remove()
        }
      }
    }

    // Initial check
    updateCSS()

    // Re-check when lock-in state might change (after form interactions)
    // This is called from setFormLockIn and clearFormLockIn
    ;(this as any).updatePullToRefreshCSS = updateCSS
  }

  // Show yellow warning message above navigation buttons
  private showRefreshWarningMessage(): void {
    // Remove existing if present
    this.hideRefreshWarningMessage()

    const warningDiv = document.createElement('div')
    warningDiv.className = 'refresh-warning-message'
    warningDiv.style.cssText = `
      margin: 20px 0;
      padding: 15px;
      background: #FEF3C7;
      border: 1px solid #F59E0B;
      border-radius: 8px;
      text-align: center;
    `
    warningDiv.innerHTML = `
      <p style="margin: 0; font-weight: 500; color: #92400E;">
        ⚠️ Please use the <strong>Back</strong> button below to edit previous information.
      </p>
      <p style="margin: 8px 0 0; color: #92400E; font-size: 14px;">
        Refreshing the page will lose your progress.
      </p>
    `

    // Insert above navigation buttons - try multiple selectors
    const navContainer = document.querySelector('[data-form="nav-buttons"]')
      || document.querySelector('.form-nav-buttons')
      || document.querySelector('[class*="button-row"]')
      || document.querySelector('[class*="nav-button"]')
      || document.querySelector('form .w-button')?.parentElement

    if (navContainer && navContainer.parentNode) {
      navContainer.parentNode.insertBefore(warningDiv, navContainer)
    } else {
      // Fallback: append to form
      const form = document.querySelector('form')
      if (form) form.appendChild(warningDiv)
    }

    this.refreshWarningElement = warningDiv

    // Auto-dismiss after 5 seconds
    setTimeout(() => this.hideRefreshWarningMessage(), 5000)

    // Also dismiss on any click
    document.addEventListener('click', () => this.hideRefreshWarningMessage(), { once: true })
  }

  // Hide the refresh warning message
  private hideRefreshWarningMessage(): void {
    if (this.refreshWarningElement) {
      this.refreshWarningElement.remove()
      this.refreshWarningElement = null
    }
  }

  // ==================== END REFRESH PREVENTION ====================

  // Bind form fields for tracking
  private bindFormFields(): void {
    const selectors = [
      'input[type="text"]',
      'input[type="email"]',
      'input[type="tel"]',
      'input[type="number"]',
      'input[type="password"]',
      'input[type="search"]',
      'input[type="url"]',
      'select',
      'textarea',
      'input[type="radio"]',
      'input[type="checkbox"]'
    ]

    const allFields = document.querySelectorAll(selectors.join(', ')) as NodeListOf<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>

    // CRITICAL: Exclude the CD additional-info outcome form's fields. That form
    // is a post-credit-decision outcome (scenarios 5/7) driven entirely by
    // AdditionalInfoFormController — its own validation, partial-save, and the
    // direct-to-dcapi submit. If the main lead-tracker also binds these fields,
    // every keystroke runs the qualification check, re-fires partial_updated,
    // and the edge function answers credit_decision — re-running the lead
    // pipeline on the outcome screen (the credit_decision spam). Skip them.
    const fields = Array.from(allFields).filter(
      (f) => !f.closest('[data-component="cd-outcome-additional-information"]')
    )

    if (this.config.debug) {
      const skipped = allFields.length - fields.length
      console.log(`📝 Found ${fields.length} form fields (skipped ${skipped} inside additional-info outcome)`)
    }

    fields.forEach((field, index) => {
      this.bindFieldEvents(field, index)
    })

    // Pre-populate form fields from query parameters
    this.prefillFromQueryParams()

    // Hide Webflow's default success/error messages
    this.hideWebflowMessages()

    // Bind co-applicant logic (show submit if "No" selected)
    this.bindCoApplicantLogic()

    // Bind step navigation buttons (Next, Back, Submit)
    this.bindStepButtons()
  }

  // Hide Webflow's default form success/error messages
  private hideWebflowMessages(): void {
    const successMessages = document.querySelectorAll('.w-form-done')
    const errorMessages = document.querySelectorAll('.w-form-fail')

    successMessages.forEach(msg => {
      const element = msg as HTMLElement
      element.style.display = 'none'
    })

    errorMessages.forEach(msg => {
      const element = msg as HTMLElement
      element.style.display = 'none'
    })

    if (this.config.debug && (successMessages.length > 0 || errorMessages.length > 0)) {
      console.log(`🙈 Hidden ${successMessages.length} success messages and ${errorMessages.length} error messages`)
    }
  }

  // Pre-populate form fields from URL query parameters
  // ONLY enabled for CreditDirect site
  private prefillFromQueryParams(): void {
    // Check site brand - only enable for CreditDirect
    const siteConfig = typeof window !== 'undefined' && (window as any).__SITE_CONFIG__
    const brand = siteConfig?.brand || 'unknown'

    if (brand !== 'creditdirect') {
      // Skip prefill for non-CreditDirect sites
      if (this.config.debug) {
        console.log(`⏭️ Query param prefill skipped (brand: ${brand}, only enabled for creditdirect)`)
      }
      return
    }

    const urlParams = new URLSearchParams(window.location.search)

    if (this.config.debug) {
      const params = Array.from(urlParams.entries())
      if (params.length > 0) {
        console.log('🔗 Query parameters detected:', params)
        console.log('🔍 Attempting to prefill form fields...')
      } else {
        console.log('ℹ️ No query parameters found in URL')
      }
    }

    // Define mapping between query parameter names and form field names
    const queryParamMappings: Record<string, string[]> = {
      'loan_amount': ['loan_amount', 'requested_loan_amount', 'loan-amount', 'amount'],
      'first_name': ['first_name', 'first-name', 'firstname', 'fname'],
      'last_name': ['last_name', 'last-name', 'lastname', 'lname'],
      'email': ['email', 'email_address', 'email-address'],
      'phone': ['phone', 'phone_number', 'phone-number', 'mobile'],
      'zip': ['zip', 'zip_code', 'zipcode', 'postal_code'],
      'debt_amount': ['debt_amount', 'total_debt', 'debt-amount']
    }

    let fieldsPopulated = 0
    let fieldsNotFound = 0

    // Iterate through query parameters
    urlParams.forEach((value, key) => {
      // Try to find matching form fields
      const possibleFieldNames = queryParamMappings[key] || [key]
      let fieldFound = false

      for (const fieldName of possibleFieldNames) {
        // Try different selector patterns
        const selectors = [
          `[name="${fieldName}"]`,
          `[id="${fieldName}"]`,
          `[data-name="${fieldName}"]`
        ]

        for (const selector of selectors) {
          const field = document.querySelector(selector) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

          if (field && !field.value) { // Only populate if field is empty
            // Set the value
            field.value = value

            // Trigger events so the system knows it's been populated
            field.dispatchEvent(new Event('input', { bubbles: true }))
            field.dispatchEvent(new Event('change', { bubbles: true }))
            field.dispatchEvent(new Event('blur', { bubbles: true }))

            fieldsPopulated++
            fieldFound = true

            if (this.config.debug) {
              console.log(`✅ Pre-filled visible field "${fieldName}" with value from ?${key}=${value}`)
            }

            // Break after finding first match
            break
          }
        }

        if (fieldFound) break
      }

      // Log if field wasn't found (this is expected for loan_amount if field was removed)
      if (!fieldFound && this.config.debug) {
        console.log(`ℹ️ No visible field found for ?${key}=${value} (will be included in form_data anyway)`)
        fieldsNotFound++
      }
    })

    if (this.config.debug) {
      if (fieldsPopulated > 0) {
        console.log(`✨ Pre-populated ${fieldsPopulated} visible field(s) from query parameters`)
      }
      if (fieldsNotFound > 0) {
        console.log(`📋 ${fieldsNotFound} query param(s) will be included in form_data even without visible fields`)
      }
    }
  }

  // Bind events to individual form fields
  private bindFieldEvents(field: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, index: number): void {
    const fieldName = this.getFieldIdentifier(field, index)
    const fieldType = this.getFieldType(field)
    const fieldId = `${fieldName}_${fieldType}_${index}`

    // Skip if already bound
    if (this.boundFields.has(fieldId)) {
      return
    }

    if (this.config.debug) {
      console.log(`🎯 Binding events for: ${fieldName} (${fieldType})`)
    }

    // Handle different event types based on field type
    if (field.type === 'radio' || field.type === 'checkbox') {
      field.addEventListener('change', (e) => this.logFieldChange(e, fieldName, fieldType, 'change'))
    } else if (field.tagName.toLowerCase() === 'select') {
      field.addEventListener('change', (e) => this.logFieldChange(e, fieldName, fieldType, 'change'))
    } else {
      // For text inputs, log on blur events (when user finishes with field)
      field.addEventListener('blur', (e) => this.logFieldChange(e, fieldName, fieldType, 'blur'))
    }

    // Log focus events for all field types
    field.addEventListener('focus', (e) => this.logFieldChange(e, fieldName, fieldType, 'focus'))

    // Add validation error detection
    field.addEventListener('invalid', (e) => this.handleValidationError(e, fieldName, fieldType))

    // Add Enter key support for text inputs (desktop UX improvement)
    if (field.type !== 'textarea' && field.type !== 'radio' && field.type !== 'checkbox') {
      field.addEventListener('keydown', (e) => this.handleEnterKeyPress(e as KeyboardEvent, fieldName))
    }

    // Mark as bound
    this.boundFields.add(fieldId)
  }

  // Handle co-applicant logic (show submit button if "No" selected)
  private bindCoApplicantLogic(): void {
    const coAppRadios = document.querySelectorAll('input[name="co_app"]')

    if (coAppRadios.length === 0) {
      if (this.config.debug) {
        console.log('ℹ️ No co-applicant radio buttons found')
      }
      return
    }

    const nextBtn = document.querySelector('[data-form="next-btn"]') as HTMLElement
    const backBtn = document.querySelector('[data-form="back-btn"]') as HTMLElement
    const submitBtn = document.querySelector('[data-form="submit-btn"]') as HTMLElement

    if (!nextBtn || !backBtn || !submitBtn) {
      if (this.config.debug) {
        console.warn('⚠️ Could not find navigation buttons for co-applicant logic')
      }
      return
    }

    // Get all co-applicant fields (they start with "coapp_")
    const coAppFields = document.querySelectorAll('input[name^="coapp_"], select[name^="coapp_"], textarea[name^="coapp_"]')

    coAppRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        const value = (e.target as HTMLInputElement).value

        if (this.config.debug) {
          console.log(`👥 Co-applicant selection changed: ${value}`)
        }

        if (value === 'co_no') {
          // User said NO to co-applicant - show submit button and remove required from co-app fields
          nextBtn.style.display = 'none'
          backBtn.style.display = 'block' // Keep back button so they can go back
          submitBtn.style.display = 'block'
          submitBtn.style.opacity = '1'
          submitBtn.style.pointerEvents = 'auto'
          submitBtn.classList.remove('disabled')

          // Remove required attribute from all co-applicant fields
          coAppFields.forEach(field => {
            field.removeAttribute('required')
          })

          if (this.config.debug) {
            console.log(`✅ Co-applicant: NO - Submit button shown, removed required from ${coAppFields.length} co-app fields`)
          }
        } else if (value === 'co_yes') {
          // User said YES to co-applicant - continue normal flow and restore required
          nextBtn.style.display = 'block'
          backBtn.style.display = 'block'
          submitBtn.style.display = 'none'

          // Restore required attribute to all co-applicant fields
          coAppFields.forEach(field => {
            field.setAttribute('required', 'required')
          })

          if (this.config.debug) {
            console.log(`✅ Co-applicant: YES - Normal flow continues, restored required to ${coAppFields.length} co-app fields`)
          }
        }
      })
    })

    if (this.config.debug) {
      console.log('👥 Co-applicant logic initialized')
    }
  }

  // Bind step navigation buttons
  private bindStepButtons(): void {
    try {
      // Look for Next/Continue buttons
      const nextButtons = document.querySelectorAll('input[data-form="next-btn"], button[data-form="next-btn"], [class*="next"], [id*="next"], input[value*="Next"], input[value*="Continue"]')
      
      // Look for Back/Previous buttons
      const backButtons = document.querySelectorAll('input[data-form="back-btn"], button[data-form="back-btn"], [class*="back"], [id*="back"], input[value*="Back"], input[value*="Previous"]')
      
      // Look for Submit buttons
      const submitButtons = document.querySelectorAll('input[data-form="submit-btn"], button[data-form="submit-btn"], input[type="submit"], button[type="submit"]')

      // Also look for buttons by text content
      const allButtons = document.querySelectorAll('button, input[type="button"], input[type="submit"]')

      allButtons.forEach(btn => {
        if (this.boundButtons.has(btn)) return

        // CRITICAL: Skip buttons inside outcome components - they should work as CMS links
        // These components have data-component attributes like "cd-outcome-unqualified", "accs-outcome-loan-only", etc.
        if (btn.closest('[data-component*="outcome"]')) {
          if (this.config.debug) {
            console.log('⏭️ Skipping button inside outcome component:', btn)
          }
          return
        }

        const text = (btn as HTMLElement).textContent?.toLowerCase() || (btn as HTMLInputElement).value?.toLowerCase() || ''

        if (text.includes('next') || text.includes('continue') || text.includes('proceed')) {
          btn.addEventListener('click', async () => {
            this.stepNumber++
            if (this.config.debug) {
              console.log(`📄 Moving to step ${this.stepNumber}`)
            }
            
            // CRITICAL: Flush all pending events before processing
            // This ensures we don't lose data when users click quickly
            await this.eventBatcher.flushAll()
            
            // ALWAYS process lead on Next button to create/update webhooks
            // This ensures we capture all form data at each step
            await this.checkLeadProcessing(true) // Pass true to force processing
          })
          this.boundButtons.add(btn)
        } else if (text.includes('back') || text.includes('previous') || text.includes('prev')) {
          btn.addEventListener('click', () => {
            this.stepNumber = Math.max(1, this.stepNumber - 1)
            if (this.config.debug) {
              console.log(`📄 Moving back to step ${this.stepNumber}`)
            }
          })
          this.boundButtons.add(btn)
        } else if (text.includes('submit') || (btn as HTMLInputElement).type === 'submit') {
          btn.addEventListener('click', async (e) => {
            e.preventDefault() // CRITICAL: Prevent default form submission

            const now = Date.now()

            // Prevent duplicate submissions within 5 seconds
            if (now - this.lastSubmissionTime < 5000) {
              if (this.config.debug) {
                console.log(`⚠️ Duplicate form submission prevented (${Math.round((now - this.lastSubmissionTime)/1000)}s since last)`)
              }
              return
            }

            this.formSubmitted = true
            this.lastSubmissionTime = now
            this.clearFormLockIn() // Clear lock-in on form submission

            // Fire Lead event on form submission (CreditDirect only)
            if (!this.hasFiredLeadEvent) {
              this.fireFacebookPixelEvent('Lead', {
                content_name: 'Application',
                content_category: 'lead_generation'
              })
              this.hasFiredLeadEvent = true
            }

            if (this.config.debug) {
              console.log(`📄 Form submission detected at ${new Date(now).toLocaleTimeString()}`)
            }
            // Process final lead submission
            await this.processLead(true)
            // Cancel any pending partial_updated requests after final submission completes
            this.cancelAllPendingRequests()
            // Trigger component renderer after form submission
            await this.triggerComponentRenderer(true)
          })
          this.boundButtons.add(btn)
        }
      })

      // Bind directly selected buttons as well
      nextButtons.forEach(btn => {
        if (!this.boundButtons.has(btn)) {
          // Skip buttons inside outcome components
          if (btn.closest('[data-component*="outcome"]')) return

          btn.addEventListener('click', async () => {
            this.stepNumber++
            // CRITICAL: Flush and force processing on Next button
            await this.eventBatcher.flushAll()
            await this.checkLeadProcessing(true) // Force processing
          })
          this.boundButtons.add(btn)
        }
      })

      backButtons.forEach(btn => {
        if (!this.boundButtons.has(btn)) {
          // Skip buttons inside outcome components
          if (btn.closest('[data-component*="outcome"]')) return

          btn.addEventListener('click', () => {
            this.stepNumber = Math.max(1, this.stepNumber - 1)
          })
          this.boundButtons.add(btn)
        }
      })

      submitButtons.forEach(btn => {
        if (!this.boundButtons.has(btn)) {
          // Skip buttons inside outcome components - they should work as CMS links
          if (btn.closest('[data-component*="outcome"]')) return

          btn.addEventListener('click', async (e) => {
            e.preventDefault() // CRITICAL: Prevent default form submission

            const now = Date.now()

            // Prevent duplicate submissions within 5 seconds
            if (now - this.lastSubmissionTime < 5000) {
              if (this.config.debug) {
                console.log(`⚠️ Duplicate submit button prevented (${Math.round((now - this.lastSubmissionTime)/1000)}s since last)`)
              }
              return
            }

            this.formSubmitted = true
            this.lastSubmissionTime = now
            this.clearFormLockIn() // Clear lock-in on form submission

            // Fire Lead event on form submission (CreditDirect only)
            if (!this.hasFiredLeadEvent) {
              this.fireFacebookPixelEvent('Lead', {
                content_name: 'Application',
                content_category: 'lead_generation'
              })
              this.hasFiredLeadEvent = true
            }

            if (this.config.debug) {
              console.log(`📄 Submit button clicked at ${new Date(now).toLocaleTimeString()}`)
            }
            await this.processLead(true)
            // Cancel any pending partial_updated requests after final submission completes
            this.cancelAllPendingRequests()
            // Trigger component renderer after submit button
            await this.triggerComponentRenderer(true)
          })
          this.boundButtons.add(btn)
        }
      })

      // Also bind to form submissions directly
      const forms = document.querySelectorAll('form')
      forms.forEach(form => {
        // CRITICAL: Skip forms inside outcome components. The CD additional-info
        // outcome is its own form (data-form="multistep") driven by
        // AdditionalInfoFormController, which POSTs to the dcapi
        // additional-information endpoint. Without this guard the lead-tracker's
        // generic submit handler ALSO fires processLead -> simpleLeadProcessor,
        // so the form double-submits to the wrong endpoint. Mirrors the
        // button-loop guard above and the MultiStepController guard.
        if (form.closest('[data-component*="outcome"]')) {
          if (this.config.debug) {
            console.log('⏭️ Skipping form inside outcome component (handled by its own controller):', form)
          }
          return
        }
        if (!this.boundButtons.has(form)) {
          form.addEventListener('submit', async (e) => {
            e.preventDefault() // CRITICAL: Always prevent default form submission

            const now = Date.now()

            // Prevent duplicate form submissions within 5 seconds
            if (now - this.lastSubmissionTime < 5000) {
              if (this.config.debug) {
                console.log(`⚠️ Duplicate form submission prevented (${Math.round((now - this.lastSubmissionTime)/1000)}s since last)`)
              }
              return
            }

            this.formSubmitted = true
            this.lastSubmissionTime = now
            this.clearFormLockIn() // Clear lock-in on form submission

            if (this.config.debug) {
              console.log(`📄 Form submit event at ${new Date(now).toLocaleTimeString()}`)
            }
            await this.processLead(true)
            // Cancel any pending partial_updated requests after final submission completes
            this.cancelAllPendingRequests()
            // Trigger component renderer after form submit event
            await this.triggerComponentRenderer(true)
          })
          this.boundButtons.add(form)
        }
      })

      // Listen for MultiStepController's Next event (capture-phase stopImmediatePropagation
      // blocks our bubble-phase click handlers, so the controller dispatches this instead)
      document.addEventListener('multiStepNext', async () => {
        this.stepNumber++
        await this.eventBatcher.flushAll()
        await this.checkLeadProcessing(true)
      })

      // Listen for MultiStepController's final submit event
      // Uses custom event instead of native 'submit' to bypass Webflow's form handling
      document.addEventListener('multiStepSubmit', async () => {
        const now = Date.now()
        if (now - this.lastSubmissionTime < 5000) return

        this.formSubmitted = true
        this.lastSubmissionTime = now
        this.clearFormLockIn()

        if (!this.hasFiredLeadEvent) {
          this.fireFacebookPixelEvent('Lead', {
            content_name: 'Application',
            content_category: 'lead_generation'
          })
          this.hasFiredLeadEvent = true
        }

        console.log('📤 multiStepSubmit received - processing final lead submission')
        await this.processLead(true)
        this.cancelAllPendingRequests()
        await this.triggerComponentRenderer(true)
      })

      // Initialize MultiStepController for any page with a multi-step form that
      // is NOT the Get My Low Rates page (which handles its own init in initializePreFill)
      if (!isGetMyLowRatesPage() && document.querySelector('[data-form="multistep"]')) {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          console.log('🎛️ Initializing MultiStepController for application form')
          const controller = new MultiStepController(this.config.debug)
          controller.initialize([])
        }))
      }

    } catch (error) {
      if (this.config.debug) {
        console.error('Error binding step buttons:', error)
      }
    }
  }

  // Log field change events
  private async logFieldChange(
    event: Event,
    fieldName: string,
    fieldType: string,
    eventType: string = 'change',
    additionalData: Partial<FormFieldData> = {}
  ): Promise<void> {
    // CRITICAL: Check for session changes on every field interaction
    this.checkAndHandleSessionChange();

    // Fire ViewContent on first form interaction (CreditDirect only)
    if (!this.hasFiredViewContent && eventType === 'focus') {
      this.fireFacebookPixelEvent('ViewContent', {
        content_name: 'Lead Form',
        content_type: 'lead_form'
      })
      this.hasFiredViewContent = true
    }

    const target = event.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    const value = this.getFieldValue(target)
    const now = Date.now()
    
    // Get previous value for comparison
    const previousData = this.fieldData
      .filter(d => d.fieldName === fieldName)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0]
    const previousValue = previousData?.value
    
    // Calculate time on field for focus/blur events
    let timeOnField: number | undefined
    if (eventType === 'focus') {
      this.fieldStartTimes.set(fieldName, now)
    } else if (eventType === 'blur') {
      const startTime = this.fieldStartTimes.get(fieldName)
      if (startTime) {
        timeOnField = now - startTime
        this.fieldStartTimes.delete(fieldName)
      }
    }
    
    // Get keystroke count
    const keystrokeCount = this.fieldKeystrokeCounts.get(fieldName) || 0
    
    // Create field data
    const data: FormFieldData = {
      fieldName,
      fieldType,
      value,
      previousValue,
      eventType,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionManager.getSessionId(),
      stepNumber: this.stepNumber,
      timeOnField,
      keystrokeCount: keystrokeCount > 0 ? keystrokeCount : undefined,
      pasteDetected: (event as any)?.paste === true || undefined,
      validationErrors: additionalData.validationErrors,
      environment: this.config.environment,
      pageUrl: window.location.href,
      formId: this.getFormId(target)
    }

    // Add to session events and local collection (always track locally for form functionality)
    this.sessionManager.addEvent(data)
    this.fieldData.push(data)

    // ALWAYS save form interactions to database - no consent blocking
    // Consent is only checked when triggering webhooks, not for data collection
    if (this.supabaseInitialized && this.isOnline) {
      const validEventTypes = ['focus', 'input', 'blur', 'change']
      if (validEventTypes.includes(eventType)) {
        const supabaseEvent: FormInteractionEvent = {
          session_id: this.sessionManager.getSessionId(),
          field_name: fieldName,
          field_type: fieldType,
          event_type: eventType as 'focus' | 'input' | 'blur' | 'change',
          field_value: value,
          form_step: this.stepNumber,
          timestamp: data.timestamp,
          sequence_number: this.sequenceNumber,
          time_on_field: timeOnField,
          keystroke_count: keystrokeCount > 0 ? keystrokeCount : undefined,
          paste_detected: data.pasteDetected,
          validation_errors: additionalData.validationErrors,
          page_url: window.location.href,
          metadata: {
            user_agent: navigator.userAgent,
            screen_resolution: `${screen.width}x${screen.height}`,
            viewport_size: `${window.innerWidth}x${window.innerHeight}`,
            scroll_position: window.scrollY,
            referrer: document.referrer,
            form_id: data.formId,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }
        }

        this.eventBatcher.addEvent(supabaseEvent)
      }
    }

    // Debug logging
    if (this.config.debug) {
      const pendingCount = this.eventBatcher.getPendingCount(this.sessionManager.getSessionId())
      console.group(`🎯 Field ${eventType.toUpperCase()}: ${fieldName}`)
      console.log(`📱 Session: ${data.sessionId.replace('sess_', '').substring(0, 8)}`)
      console.log(`📊 Value: "${value}" ${previousValue ? `(was: "${previousValue}")` : ''}`)
      console.log(`📄 Step: ${this.stepNumber}`)
      console.log(`📤 Pending: ${pendingCount}`)
      if (timeOnField) console.log(`⏱️  Time on field: ${Math.round(timeOnField / 1000)}s`)
      console.groupEnd()
    }

    // Reset keystroke count after logging
    if (eventType === 'blur' || eventType === 'change') {
      this.fieldKeystrokeCounts.set(fieldName, 0)
    }

    this.sequenceNumber++

    // Process lead on meaningful field changes (with debouncing)
    // IMPORTANT: Exclude checkboxes - they should only trigger webhooks on Next button click
    const isCheckbox = fieldType === 'checkbox'
    if ((eventType === 'blur' || eventType === 'change') && value && value.trim().length > 0 && !isCheckbox) {
      // Debounce rapid field changes to prevent race conditions
      if (this.processingDebounceTimer) {
        clearTimeout(this.processingDebounceTimer)
      }

      this.processingDebounceTimer = setTimeout(async () => {
        await this.checkLeadProcessing()
      }, 500) // 500ms debounce
    }
  }

  // Handle validation errors
  private handleValidationError(event: Event, fieldName: string, fieldType: string): void {
    const target = event.target as HTMLInputElement
    const validationMessage = target.validationMessage

    // Log validation errors as 'blur' events with validation data
    this.logFieldChange(event, fieldName, fieldType, 'blur', {
      validationErrors: { [fieldName]: validationMessage }
    })
  }

  // Handle Enter key press on form fields (desktop UX improvement)
  private async handleEnterKeyPress(event: KeyboardEvent, fieldName: string): Promise<void> {
    // Only handle Enter key
    if (event.key !== 'Enter') {
      return
    }

    if (this.config.debug) {
      console.log(`⌨️ Enter key pressed on field: ${fieldName}`)
    }

    // Prevent default form submission behavior
    event.preventDefault()

    // Check if Next button is enabled (validation passed)
    const nextButtonEnabled = this.isNextButtonEnabled()

    if (!nextButtonEnabled) {
      if (this.config.debug) {
        console.log('⏸️ Next button disabled - Enter key ignored (validation not passed)')
      }
      return
    }

    if (this.config.debug) {
      console.log('✅ Next button enabled - triggering progression via Enter key')
    }

    // Find the Next button and click it
    const nextButton = this.findNextButton()
    if (nextButton) {
      // Trigger blur on current field first to save data
      const target = event.target as HTMLInputElement
      target.blur()

      // Small delay to allow blur event to process
      await new Promise(resolve => setTimeout(resolve, 100))

      // Click the Next button
      nextButton.click()

      if (this.config.debug) {
        console.log('🚀 Next button clicked via Enter key')
      }
    } else {
      if (this.config.debug) {
        console.warn('⚠️ Next button not found in DOM')
      }
    }
  }

  // Find the Next button in the DOM
  private findNextButton(): HTMLElement | null {
    // Try specific selectors first
    const specificButton = document.querySelector(
      'input[data-form="next-btn"], button[data-form="next-btn"]'
    ) as HTMLElement

    if (specificButton && this.isButtonClickable(specificButton)) {
      return specificButton
    }

    // Try finding by value/text content
    const allButtons = document.querySelectorAll('button, input[type="button"], input[type="submit"]')

    for (const btn of allButtons) {
      const button = btn as HTMLElement
      const text = (button.textContent?.trim() || (button as HTMLInputElement).value || '').toLowerCase()

      if ((text.includes('next') || text.includes('continue')) && this.isButtonClickable(button)) {
        return button
      }
    }

    return null
  }

  // Check if a button is clickable (not disabled, not hidden, etc.)
  private isButtonClickable(button: HTMLElement): boolean {
    const inputButton = button as HTMLButtonElement | HTMLInputElement

    // Check standard disabled attribute
    if (inputButton.disabled) {
      return false
    }

    // Check display style
    if (button.style.display === 'none') {
      return false
    }

    // Check CSS classes
    if (button.classList.contains('disabled') ||
        button.classList.contains('is-disabled') ||
        button.classList.contains('btn-disabled')) {
      return false
    }

    // Check inline styles
    const opacity = button.style.opacity ? parseFloat(button.style.opacity) : 1
    const pointerEvents = button.style.pointerEvents

    if (opacity < 0.5 || pointerEvents === 'none') {
      return false
    }

    return true
  }

  // Check if lead processing should be triggered (queued to prevent race conditions)
  private async checkLeadProcessing(forceProcess: boolean = false): Promise<void> {
    // CRITICAL: Always check for session changes before processing
    this.checkAndHandleSessionChange();
    // Add to processing queue to ensure sequential execution
    this.processingQueue = this.processingQueue.then(async () => {
      return this.executeLeadProcessingCheck(forceProcess)
    }).catch(error => {
      console.error('Error in lead processing queue:', error)
    })
    
    return this.processingQueue
  }
  
  // Execute the actual lead processing check (called sequentially from queue)
  private async executeLeadProcessingCheck(forceProcess: boolean = false): Promise<void> {
    // Prevent overlapping calls
    if (this.isProcessing) {
      if (this.config.debug) {
        console.log('⏸️ Lead processing already in progress - skipping')
      }
      return
    }
    
    this.isProcessing = true
    
    try {
      const now = Date.now()
      const leadData = this.extractLeadData()
      const isQualified = this.isQualifiedPartialLead()
      
      // Only check Next button for webhook triggering (not for data saving)
      const nextButtonEnabled = this.isNextButtonEnabled()
      
      // Debug logging
      if (this.config.debug) {
        console.log('🔍 Lead Processing Check:', {
          hasPartialCreatedWebhook: this.hasPartialCreatedWebhook,
          isQualified,
          nextButtonEnabled,
          isProcessing: this.isProcessing,
          firstName: leadData.first_name,
          lastName: leadData.last_name, 
          email: leadData.email,
          phone: leadData.phone
        })
      }
      
      // Only block WEBHOOK processing if Next button not enabled AND not force-triggered
      // forceProcess=true means multiStepNext fired (MultiStepController already validated the step)
      // so we always proceed regardless of button visibility in that case
      if (!nextButtonEnabled && !forceProcess) {
        if (this.config.debug) {
          console.log('⏸️ Webhook processing skipped - Next button not enabled')
        }
        return
      }
      
      // ALWAYS process on Next button click (forceProcess) or based on conditions
      if (forceProcess) {
        // Force processing on every Next button click - REAL-TIME UPDATES
        if (!this.hasPartialCreatedWebhook && isQualified) {
          if (this.config.debug) {
            console.log('🎯 Triggering partial_created webhook (Next button + qualified)')
          }
          await this.processLead(false, true) // Pass true for is_first_qualified
          this.hasPartialCreatedWebhook = true
          this.setFormLockIn() // Lock in user - prevent accidental refresh
        } else if (this.hasPartialCreatedWebhook) {
          // Fire partial_updated on EVERY Next button click after partial_created
          if (this.config.debug) {
            console.log('🎯 Triggering partial_updated webhook (Next button click - real-time)')
          }
          await this.processLead(false)
          this.lastUpdateTime = now
        }
      } else {
        // Original conditional logic when not forced
        if (!this.hasPartialCreatedWebhook && isQualified) {
          if (this.config.debug) {
            console.log('🎯 Triggering partial_created webhook (has phone + terms checked)')
          }
          await this.processLead(false, true)
          this.hasPartialCreatedWebhook = true
          this.setFormLockIn() // Lock in user - prevent accidental refresh
        } else if (this.hasPartialCreatedWebhook) {
          // REMOVED: throttling and significant update checks for real-time updates
          if (this.config.debug) {
            console.log('🎯 Triggering partial_updated lead processing (real-time update)')
          }
          await this.processLead(false)
          this.lastUpdateTime = now
        } else if (this.config.debug) {
          console.log('⏸️ Lead processing conditions not met - no webhook fired')
        }
      }
    } finally {
      this.isProcessing = false
    }
  }

  // Check if we have qualified partial lead data (first_name + last_name + email + phone)
  // AND the tcapAuth checkbox is checked (for partial_created webhook)
  private isQualifiedPartialLead(): boolean {
    const leadData = this.extractLeadData()

    // Must have first_name + last_name + email + phone for qualified partial lead
    const hasFirstName = !!leadData.first_name && leadData.first_name.trim().length > 0
    const hasLastName = !!leadData.last_name && leadData.last_name.trim().length > 0
    const hasEmail = !!leadData.email && leadData.email.trim().length > 0
    const hasPhone = !!leadData.phone && leadData.phone.trim().length > 0

    // Check for tcapAuth checkbox (new name) or fallback to old "terms" name
    const tcapAuthCheckbox = document.querySelector('input[type="checkbox"][name="tcapAuth"], input[type="checkbox"][id="tcapAuth"]') as HTMLInputElement
    const termsCheckbox = document.querySelector('input[type="checkbox"][name="terms"]') as HTMLInputElement
    const consentCheckbox = tcapAuthCheckbox || termsCheckbox
    const consentChecked = consentCheckbox ? consentCheckbox.checked : false

    if (this.config.debug && hasFirstName && hasLastName && hasEmail && hasPhone) {
      console.log('📋 Qualification check:', {
        hasAllFields: true,
        consentChecked,
        checkboxFound: !!consentCheckbox,
        checkboxName: consentCheckbox?.name || consentCheckbox?.id || 'none',
        willTriggerWebhook: consentChecked
      })
    }

    // All fields AND tcapAuth/terms must be checked for webhook
    return hasFirstName && hasLastName && hasEmail && hasPhone && consentChecked
  }
  
  // Check if there are any consent checkboxes on the current form
  private hasConsentCheckboxes(): boolean {
    const consentCheckboxes = document.querySelectorAll(
      'input[type="checkbox"][required], ' +
      'input[name*="terms"], ' +
      'input[name*="consent"], ' +
      'input[name*="agree"], ' +
      'input[name*="privacy"], ' +
      'input[name*="accept"], ' +
      'input[name="tcapAuth"], ' +
      'input[id="tcapAuth"], ' +
      'input[name="smsAuth"], ' +
      'input[id="smsAuth"]'
    )
    return consentCheckboxes.length > 0
  }
  
  // CRITICAL: Check if required form validations pass (terms, conditions, etc.)
  private areRequiredFieldsValid(): boolean {
    try {
      // PRIORITY CHECK: Look for consent/terms checkboxes specifically
      // These are the critical privacy compliance checkboxes
      const consentCheckboxes = document.querySelectorAll(
        'input[type="checkbox"][required], ' +
        'input[name*="terms"], ' +
        'input[name*="consent"], ' +
        'input[name*="agree"], ' +
        'input[name*="privacy"], ' +
        'input[name*="accept"]'
      )
      
      // If there are consent checkboxes, at least one must be checked
      let hasConsentCheckboxes = false
      let consentGiven = false
      
      for (const checkbox of consentCheckboxes) {
        const cb = checkbox as HTMLInputElement
        hasConsentCheckboxes = true
        
        // If it's required and not checked, no consent
        if (cb.required && !cb.checked) {
          if (this.config.debug) {
            console.log(`❌ Required consent checkbox not checked: ${cb.name || cb.id}`)
          }
          return false
        }
        
        // If any consent checkbox is checked, we have consent
        if (cb.checked) {
          consentGiven = true
        }
      }
      
      // If there are consent checkboxes but none are checked, no consent
      if (hasConsentCheckboxes && !consentGiven) {
        if (this.config.debug) {
          console.log('❌ No consent checkboxes are checked')
        }
        return false
      }
      
      // Also check if current form step has all required fields filled
      // (but this is secondary to consent)
      const currentForm = this.getCurrentFormSection()
      if (currentForm) {
        const requiredFields = currentForm.querySelectorAll('input[required]:not([type="checkbox"]), select[required], textarea[required]')
        for (const field of requiredFields) {
          const input = field as HTMLInputElement
          if (!input.value || !input.validity.valid) {
            if (this.config.debug) {
              console.log(`❌ Required field invalid: ${input.name || input.id}`)
            }
            return false
          }
        }
      }
      
      return true
    } catch (error) {
      if (this.config.debug) {
        console.warn('Error checking required fields:', error)
      }
      return false // Fail safe - don't save data if we can't validate
    }
  }
  
  // Check if Next button is enabled (user can actually proceed)
  private isNextButtonEnabled(): boolean {
    try {
      // More specific selectors for Next buttons (removed invalid :has-text selector)
      const nextButtons = document.querySelectorAll(
        'input[data-form="next-btn"], ' +
        'button[data-form="next-btn"], ' +
        'input[type="button"][value="Next"], ' +
        'input[type="submit"][value="Next"], ' +
        'button[value="Next"], ' +
        'input[value="Continue"], ' +
        'button[value="Continue"], ' +
        'button[id*="next-btn"], ' +
        'input[id*="next-btn"], ' +
        'button[id*="next"], ' +
        'input[class*="next"], ' +
        'button[class*="next"]'
      )
      
      // Also check all buttons for text content containing "Next"
      const allButtons = document.querySelectorAll('button, input[type="button"], input[type="submit"]')
      const additionalNextButtons: Element[] = []
      
      for (const btn of allButtons) {
        const buttonText = (btn.textContent?.trim() || (btn as HTMLInputElement).value || '').toLowerCase()
        if (buttonText.includes('next') || buttonText.includes('continue')) {
          // Check if not already in nextButtons NodeList
          let alreadyIncluded = false
          for (const existing of nextButtons) {
            if (existing === btn) {
              alreadyIncluded = true
              break
            }
          }
          if (!alreadyIncluded) {
            additionalNextButtons.push(btn)
          }
        }
      }
      
      // Combine both lists
      const allNextButtons = [...Array.from(nextButtons), ...additionalNextButtons]
      
      // If we find next buttons, check if any are enabled
      if (allNextButtons.length > 0) {
        if (this.config.debug) {
          console.log(`🔍 Found ${allNextButtons.length} potential Next button(s)`)
        }
        
        for (const btn of allNextButtons) {
          const button = btn as HTMLInputElement | HTMLButtonElement
          
          if (this.config.debug) {
            console.log(`  Checking button:`, {
              type: button.type,
              value: (button as HTMLInputElement).value,
              className: button.className,
              disabled: button.disabled,
              opacity: button.style.opacity,
              pointerEvents: button.style.pointerEvents
            })
          }
          
          // Check multiple ways buttons can be disabled:
          // 1. Standard HTML disabled attribute
          if (button.disabled) {
            continue
          }
          
          // 2. Hidden via display style
          if (button.style.display === 'none') {
            continue
          }
          
          // 3. Webflow's CSS class-based disabling
          if (button.classList.contains('disabled') || 
              button.classList.contains('is-disabled') ||
              button.classList.contains('btn-disabled')) {
            if (this.config.debug) {
              console.log(`❌ Next button has disabled class: ${button.className}`)
            }
            continue
          }
          
          // 4. Webflow's inline style disabling (opacity and pointer-events)
          const opacity = button.style.opacity ? parseFloat(button.style.opacity) : 1
          const pointerEvents = button.style.pointerEvents
          
          // Check for explicit disabled state
          if (opacity < 0.5 || pointerEvents === 'none') {
            if (this.config.debug) {
              console.log(`❌ Next button disabled via styles - opacity: ${opacity}, pointer-events: ${pointerEvents}`)
            }
            continue
          }
          
          // Also check for explicit enabled state (Webflow sets pointer-events: auto when enabled)
          if (pointerEvents && pointerEvents !== 'auto' && pointerEvents !== '') {
            if (this.config.debug) {
              console.log(`❌ Next button has non-auto pointer-events: ${pointerEvents}`)
            }
            continue
          }
          
          // 5. Check computed styles (in case styles are set via CSS)
          const computedStyle = window.getComputedStyle(button)
          const computedOpacity = parseFloat(computedStyle.opacity)
          const computedPointerEvents = computedStyle.pointerEvents
          
          if (computedOpacity < 0.5 || computedPointerEvents === 'none') {
            if (this.config.debug) {
              console.log(`❌ Next button disabled via computed styles - opacity: ${computedOpacity}, pointer-events: ${computedPointerEvents}`)
            }
            continue
          }
          
          // If we made it here, button is enabled
          if (this.config.debug) {
            console.log(`✅ Next button IS ENABLED:`, {
              value: (button as HTMLInputElement).value,
              className: button.className,
              opacity: opacity,
              pointerEvents: pointerEvents || 'default'
            })
          }
          return true
        }
        
        // All next buttons are disabled
        if (this.config.debug) {
          console.log('❌ All Next buttons are disabled')
        }
        return false
      }
      
      // No next buttons found - be conservative and block
      if (this.config.debug) {
        console.log('⚠️ No Next buttons found - blocking data save for safety')
      }
      return false
    } catch (error) {
      if (this.config.debug) {
        console.warn('Error checking next button state:', error)
      }
      return false // Fail safe - don't save data if we can't check button state
    }
  }
  
  // Get current form section/step
  private getCurrentFormSection(): Element | null {
    try {
      // Look for active/visible form sections
      const sections = document.querySelectorAll('[data-form-step], .form-step, .step, .section')
      for (const section of sections) {
        const el = section as HTMLElement
        if (el.style.display !== 'none' && !el.hidden) {
          return section
        }
      }
      
      // Fallback to main form
      return document.querySelector('form')
    } catch (error) {
      return document.querySelector('form')
    }
  }

  // Check if we have minimum data for lead processing (legacy method for compatibility)
  private hasMinimumData(): boolean {
    return this.isQualifiedPartialLead()
  }

  // Check if we have significant updates since last processing
  private hasSignificantUpdate(): boolean {
    const currentData = this.extractFormData()
    const fieldCount = Object.keys(currentData).length
    const hasNewImportantField = this.config.leadDetection.importantFields.some(field => 
      !!(currentData as any)[field] && (currentData as any)[field] !== (this.lastSentData as any)?.[field]
    )
    
    return fieldCount > (this.lastSentFieldCount || 0) || hasNewImportantField
  }

  // Process lead via simple-lead-processor edge function
  private async processLead(isComplete: boolean = false, isFirstQualified: boolean = false): Promise<void> {
    // CRITICAL: Always check session before processing
    this.checkAndHandleSessionChange()

    if (!this.supabaseInitialized || !this.isOnline) {
      return
    }

    // Determine webhook type for logging
    const webhookType = isComplete ? 'credit_decision' :
                        isFirstQualified ? 'partial_created' :
                        this.hasPartialCreatedWebhook ? 'partial_updated' : 'partial_created'

    // Create AbortController for this request
    const abortController = new AbortController()
    this.pendingRequests.add(abortController)

    try {
      const formData = this.extractFormData()
      // Get brand from site config (set by entry point)
      const siteConfig = typeof window !== 'undefined' && (window as any).__SITE_CONFIG__
      const brand = siteConfig?.brand || 'unknown'

      const trackingData = {
        ...this.extractAllQueryParameters(),
        ...this.getBrowserInfo(),
        landing_page: window.location.href,
        referrer: document.referrer || undefined,
        page_url: window.location.href,
        brand: brand, // Site/brand identifier for multi-site tracking
        // Google Ads tracking parameters
        gclid: getGclid(),
        gcid: getGcid(),
        // Facebook Pixel tracking parameters
        fbp: getFbp(),
        fbc: getFbc(),
        // Facebook campaign tracking parameters (cleaner names)
        fb_campaign_id: getFbCampaignId(),
        fb_adset_id: getFbAdsetId(),
        fb_ad_id: getFbAdId(),
        fb_adgroup: getFbAdgroup(),
        fb_creative: getFbCreative(),
        external_transaction_id: this.refId || cleanSessionId(this.sessionManager.getSessionId()),
        // Pre-fill tracking (Get My Low Rates page)
        refId: this.refId || undefined,
        isPreFillSubmission: !!this.prefilledData
      }

      logger.info(LogCategory.API, `Processing lead: ${webhookType}`, {
        session_id: this.sessionManager.getSessionId(),
        webhook_type: webhookType,
        is_complete: isComplete,
        is_first_qualified: isFirstQualified,
        field_count: Object.keys(formData).length,
        brand
      })

      // Capture LogRocket URL before sending (with short timeout so it never blocks)
      let logRocketUrl: string | undefined
      if (isFirstQualified && typeof window !== 'undefined' && (window as any).LogRocket) {
        logRocketUrl = await new Promise<string | undefined>((resolve) => {
          const timer = setTimeout(() => resolve(undefined), 800)
          ;(window as any).LogRocket.getSessionURL((url: string) => {
            clearTimeout(timer)
            resolve(url || undefined)
          })
        })
      }

      const response = await fetch(config.edgeFunctions.endpoints.simpleLeadProcessor, {
        method: 'POST',
        headers: getApiHeaders(config),
        body: JSON.stringify({
          session_id: this.sessionManager.getSessionId(),
          form_data: formData,
          is_complete: isComplete || this.formSubmitted,
          is_first_qualified: isFirstQualified,
          tracking_data: trackingData,
          ...(logRocketUrl ? { logrocket_url: logRocketUrl } : {})
        }),
        signal: abortController.signal
      })

      const result = await response.json()

      if (result.success) {
        logger.info(LogCategory.API, `Lead processed: ${result.event_type}`, {
          session_id: this.sessionManager.getSessionId(),
          event_type: result.event_type,
          success: true
        })

        if (this.config.debug) {
          console.log(`🎯 ${result.event_type} - webhook fired successfully!`)
        }

        // Track form submission in GTM when complete
        if (isComplete) {
          trackFormSubmission(this.sessionManager.getSessionId(), brand)
        }

        // External webhooks are now called from the Supabase edge function (server-side)
        // No need to call from frontend to avoid CORS issues

        // Fire Contact event when contact info is provided (Step 1 complete) - CreditDirect only
        if (!this.hasFiredContactEvent && isFirstQualified) {
          // Hash contact information per Facebook standards
          const hashedEmail = await this.hashSHA256(formData.email || '')
          const hashedPhone = await this.hashSHA256(formData.phone || '')
          const hashedFirstName = await this.hashSHA256(formData.first_name || formData['first-name'] || '')
          const hashedLastName = await this.hashSHA256(formData.last_name || formData['last-name'] || '')

          this.fireFacebookPixelEvent('Contact', {
            content_name: 'Contact Info Provided',
            // Advanced matching with hashed PII
            em: hashedEmail,
            ph: hashedPhone,
            fn: hashedFirstName,
            ln: hashedLastName
          })

          // Also log to Supabase via our logger
          trackFBContact(brand)
          this.hasFiredContactEvent = true
        }

        // Identify user in LogRocket as soon as partial_created fires (name + email available)
        // This captures recordings for partial completions, not just full submissions
        if (isFirstQualified && typeof window !== 'undefined' && (window as any).LogRocket) {
          try {
            const sessionId = this.sessionManager.getSessionId().replace(/^sess_/, '')
            const firstName = formData.first_name || (formData as any)['first-name'] || ''
            const lastName = formData.last_name || (formData as any)['last-name'] || ''
            const traits: Record<string, any> = {
              'Session ID': sessionId,
              'Brand': brand,
            }
            if (firstName || lastName) {
              traits['name'] = `${firstName} ${lastName}`.trim()
            }
            if (formData.email) {
              traits['email'] = formData.email
            }
            ;(window as any).LogRocket.identify(sessionId, traits)

            if (this.config.debug) {
              console.log('📊 LogRocket early identify sent (partial_created):', traits)
            }
          } catch (e) {
            // Non-critical
          }
        }

        // Store data for comparison
        this.lastSentData = { ...formData }
        this.lastSentFieldCount = Object.keys(formData).length

        return result
      } else {
        logger.warn(LogCategory.API, 'Lead processing failed', {
          session_id: this.sessionManager.getSessionId(),
          webhook_type: webhookType,
          error: result.error
        })
        console.warn('⚠️ Lead processing failed:', result.error)
      }
    } catch (error) {
      // Check if error is due to abort
      if (error instanceof Error && error.name === 'AbortError') {
        logger.debug(LogCategory.API, 'Lead processing cancelled', {
          session_id: this.sessionManager.getSessionId(),
          webhook_type: webhookType
        })
        if (this.config.debug) {
          console.log('🛑 Lead processing request cancelled')
        }
        return // Don't log as error - this is intentional
      }
      logger.error(LogCategory.API, 'Lead processing error', {
        session_id: this.sessionManager.getSessionId(),
        webhook_type: webhookType,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      console.error('❌ Lead processing error:', error)
    } finally {
      // Remove from pending requests
      this.pendingRequests.delete(abortController)
    }
  }

  // Extract form data from interactions AND current DOM state
  private extractFormData(): Record<string, any> {
    const formData: Record<string, any> = {}
    
    // FIRST: Get all current form values directly from the DOM
    // This ensures we capture everything, even if events haven't fired yet
    const allInputs = document.querySelectorAll(
      'input[type="text"], input[type="email"], input[type="tel"], ' +
      'input[type="number"], input[type="date"], input[type="url"], ' +
      'input[type="search"], input[type="password"], ' +
      'select, textarea, input[type="radio"]:checked, input[type="checkbox"]'
    )
    
    allInputs.forEach(input => {
      const field = input as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      const fieldName = field.name || field.id
      
      if (!fieldName) return
      
      // Handle different input types
      if (field.type === 'checkbox') {
        const checked = (field as HTMLInputElement).checked
        formData[fieldName] = checked
      } else if (field.type === 'radio') {
        // Radio buttons - only include if checked
        formData[fieldName] = field.value
      } else if (field.value && field.value.trim()) {
        // All other inputs - only include if has value
        formData[fieldName] = field.value.trim()
      }
    })
    
    // SECOND: Override with interaction data (more accurate for timing/changes)
    // This ensures we use the most recent interaction data if available
    for (const interaction of this.fieldData) {
      if ((interaction.eventType === 'blur' || interaction.eventType === 'change') && interaction.value) {
        if (interaction.value !== 'unchecked') {
          formData[interaction.fieldName] = interaction.value
        }
      }
    }
    
    // THIRD: Always check specific checkboxes directly
    const termsCheckbox = document.querySelector('input[type="checkbox"][name="terms"]') as HTMLInputElement
    const tcapAuthCheckbox = document.querySelector('input[type="checkbox"][name="tcapAuth"], input[type="checkbox"][id="tcapAuth"]') as HTMLInputElement
    const smsAuthCheckbox = document.querySelector('input[type="checkbox"][name="smsAuth"], input[type="checkbox"][id="smsAuth"]') as HTMLInputElement
    const softPullCheckbox = document.querySelector('input[type="checkbox"][name="soft_pull"]') as HTMLInputElement

    if (termsCheckbox) {
      formData['terms'] = termsCheckbox.checked
    }
    if (tcapAuthCheckbox) {
      formData['tcapAuth'] = tcapAuthCheckbox.checked
    }
    if (smsAuthCheckbox) {
      formData['smsAuth'] = smsAuthCheckbox.checked
    }
    if (softPullCheckbox) {
      formData['soft_pull'] = softPullCheckbox.checked
    }

    // FOURTH: For CreditDirect only - include query parameters in form data
    // This ensures values like loan_amount are included even if field doesn't exist in form
    const siteConfig = typeof window !== 'undefined' && (window as any).__SITE_CONFIG__
    const brand = siteConfig?.brand || 'unknown'

    if (brand === 'creditdirect') {
      const urlParams = new URLSearchParams(window.location.search)

      // Define important query parameters to include in form_data
      const importantQueryParams = ['loan_amount', 'debt_amount', 'first_name', 'last_name', 'email', 'phone', 'zip']

      importantQueryParams.forEach(paramName => {
        const paramValue = urlParams.get(paramName)
        // Only add if: 1) parameter exists, 2) has value, 3) not already in formData
        if (paramValue && paramValue.trim() && !formData[paramName]) {
          formData[paramName] = paramValue.trim()

          if (this.config.debug) {
            console.log(`📋 Added query param to form_data: ${paramName} = ${paramValue}`)
          }
        }
      })
    }

    // FIFTH: Merge with pre-filled data (if exists)
    // Pre-filled data acts as BASE - user input OVERRIDES it
    if (this.prefilledData) {
      const mergedData = { ...this.prefilledData, ...formData }

      if (this.config.debug) {
        console.log('📋 Merged pre-filled data with user input:', {
          prefilledFields: Object.keys(this.prefilledData).length,
          userInputFields: Object.keys(formData).length,
          mergedFields: Object.keys(mergedData).length
        })
      }

      return mergedData
    }

    return formData
  }

  // Extract lead data with field mapping
  public extractLeadData(): LeadData {
    const leadData: LeadData = {
      session_id: this.sessionManager.getSessionId()
    }

    const formData = this.extractFormData()

    // Map form fields to lead data structure
    for (const [fieldName, value] of Object.entries(formData)) {
      const lowerFieldName = fieldName.toLowerCase()
      
      // Name detection
      if (lowerFieldName.includes('first') && lowerFieldName.includes('name')) {
        leadData.first_name = value as string
      } else if (lowerFieldName.includes('last') && lowerFieldName.includes('name')) {
        leadData.last_name = value as string
      } else if (lowerFieldName === 'email') {
        leadData.email = value as string
      } else if (lowerFieldName === 'phone') {
        leadData.phone = value as string
      } else if (lowerFieldName.includes('loan') && lowerFieldName.includes('amount')) {
        leadData.loan_amount = parseFloat(value as string) || undefined
      } else if (lowerFieldName.includes('income')) {
        leadData.annual_income = parseFloat(value as string) || undefined
      } else if (lowerFieldName.includes('employment')) {
        leadData.employment_status = value as string
      } else if (lowerFieldName.includes('zip')) {
        // Check ZIP first before generic address check (prevents address_zip from matching "address")
        leadData.address_zip = value as string
      } else if (lowerFieldName.includes('city')) {
        leadData.address_city = value as string
      } else if (lowerFieldName.includes('state')) {
        leadData.address_state = value as string
      } else if (lowerFieldName.includes('address') || lowerFieldName.includes('street')) {
        // Check generic address/street AFTER specific address components
        leadData.address_street = value as string
      } else if (lowerFieldName.includes('housing')) {
        if (lowerFieldName.includes('payment')) {
          leadData.monthly_housing_payment = parseFloat(value as string) || undefined
        } else {
          leadData.housing_status = value as string
        }
      } else if (lowerFieldName.includes('ssn')) {
        leadData.ssn = value as string
      } else if (lowerFieldName.includes('birth') || lowerFieldName.includes('dob')) {
        leadData.date_of_birth = value as string
      }
    }

    // Add additional info
    const completionPercentage = this.calculateCompletionPercentage()
    const sessionMetadata = this.sessionManager.getMetadata()
    
    leadData.additional_info = {
      form_completion_percentage: completionPercentage,
      total_interactions: this.fieldData.length,
      time_on_form: this.sessionManager.getSessionDuration(),
      unique_fields_touched: new Set(this.fieldData.map(d => d.fieldName)).size,
      session_metadata: sessionMetadata
    }

    return leadData
  }

  // Calculate form completion percentage
  private calculateCompletionPercentage(): number {
    const allFields = new Set(this.fieldData.map(d => d.fieldName))
    if (allFields.size === 0) return 0
    
    // Count fields with actual input (blur events with values OR change events with values)
    const completedFields = new Set<string>()
    
    for (const interaction of this.fieldData) {
      if (interaction.eventType === 'blur' && interaction.value && interaction.value.trim().length > 0) {
        completedFields.add(interaction.fieldName)
      } else if (interaction.eventType === 'change' && interaction.value && interaction.value.trim().length > 0 && interaction.value !== 'unchecked') {
        completedFields.add(interaction.fieldName)
      }
    }
    
    return Math.round((completedFields.size / allFields.size) * 100)
  }

  // Visual indicator for debugging (development only)
  private addVisualIndicator(): void {
    const sessionId = this.sessionManager.getSessionId()
    const shortId = sessionId.replace('sess_', '').substring(0, 8)
    
    const indicator = document.createElement('div')
    const pendingCount = this.eventBatcher.getPendingCount(sessionId)
    const statusIcon = this.isOnline ? '🌐' : '📵'
    const supabaseIcon = this.supabaseInitialized ? '✅' : '⚠️'
    
    indicator.innerHTML = `
      📊 Session: ${shortId}<br/>
      Env: ${this.config.environment}<br/>
      ${statusIcon} ${this.isOnline ? 'Online' : 'Offline'}<br/>
      ${supabaseIcon} Supabase<br/>
      📤 Pending: ${pendingCount}
    `
    indicator.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      background: ${this.config.environment === 'development' ? '#2196F3' : '#4CAF50'};
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      z-index: 9999;
      font-family: Arial, sans-serif;
      font-size: 10px;
      line-height: 1.2;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      cursor: pointer;
      user-select: none;
      max-width: 120px;
    `

    // Add click handler for session details
    indicator.addEventListener('click', () => {
      const session = this.sessionManager.getSession()
      if (session) {
        console.group('📱 Unified Lead Tracker Session Details')
        console.log('Session ID:', session.id)
        console.log('Created:', new Date(session.createdAt).toLocaleString())
        console.log('Duration:', Math.round(this.sessionManager.getSessionDuration() / 1000), 'seconds')
        console.log('Local Events:', this.fieldData.length)
        console.log('Pending Events:', this.eventBatcher.getPendingCount(sessionId))
        console.log('Form Completion:', this.calculateCompletionPercentage() + '%')
        console.log('Online Status:', this.isOnline)
        console.log('Supabase Status:', this.supabaseInitialized ? 'Connected' : 'Disconnected')
        console.groupEnd()
      }
    })

    if (document.body) {
      document.body.appendChild(indicator)
      
      // Update indicator periodically
      setInterval(() => {
        const newPendingCount = this.eventBatcher.getPendingCount(sessionId)
        const newStatusIcon = this.isOnline ? '🌐' : '📵'
        const newSupabaseIcon = this.supabaseInitialized ? '✅' : '⚠️'
        
        indicator.innerHTML = `
          📊 Session: ${shortId}<br/>
          Env: ${this.config.environment}<br/>
          ${newStatusIcon} ${this.isOnline ? 'Online' : 'Offline'}<br/>
          ${newSupabaseIcon} Supabase<br/>
          📤 Pending: ${newPendingCount}
        `
      }, 5000)
      
      // Fade after 5 seconds but keep visible
      setTimeout(() => {
        indicator.style.opacity = '0.6'
      }, 5000)
    }
  }

  // Extract UTM parameters from current URL
  // Extract ALL query parameters from URL
  private extractAllQueryParameters(): Record<string, any> {
    const urlParams = new URLSearchParams(window.location.search)
    const allParams: Record<string, any> = {}
    
    // Capture all query parameters
    for (const [key, value] of urlParams.entries()) {
      allParams[key] = value
    }
    
    // Also extract specific UTM parameters for easy access
    const utmParams = {
      utm_source: urlParams.get('utm_source') || undefined,
      utm_medium: urlParams.get('utm_medium') || undefined,
      utm_campaign: urlParams.get('utm_campaign') || undefined,
      utm_term: urlParams.get('utm_term') || undefined,
      utm_content: urlParams.get('utm_content') || undefined
    }
    
    return {
      ...allParams,
      ...utmParams,
      all_query_params: allParams // Store complete params object for reference
    }
  }

  // Extract browser and device info
  private getBrowserInfo(): Record<string, string | undefined> {
    const userAgent = navigator.userAgent

    return {
      user_agent: userAgent,
      screen_resolution: `${screen.width}x${screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language || undefined,
      device_type: this.detectDeviceType(userAgent),
      browser: this.detectBrowser(userAgent),
      os: this.detectOS(userAgent)
    }
  }

  // Device detection from user agent
  private detectDeviceType(userAgent: string): string {
    if (!userAgent) return 'unknown'
    
    const ua = userAgent.toLowerCase()
    
    if (/mobile|android|iphone|ipod|blackberry|windows phone/i.test(ua)) {
      return 'mobile'
    } else if (/tablet|ipad/i.test(ua)) {
      return 'tablet'
    } else {
      return 'desktop'
    }
  }

  // Browser detection from user agent
  private detectBrowser(userAgent: string): string {
    if (!userAgent) return 'unknown'
    
    const ua = userAgent.toLowerCase()
    
    if (ua.includes('chrome') && !ua.includes('edg') && !ua.includes('opr')) {
      return 'chrome'
    } else if (ua.includes('firefox')) {
      return 'firefox'
    } else if (ua.includes('safari') && !ua.includes('chrome')) {
      return 'safari'
    } else if (ua.includes('edg')) {
      return 'edge'
    } else if (ua.includes('opr')) {
      return 'opera'
    } else if (ua.includes('trident') || ua.includes('msie')) {
      return 'internet_explorer'
    } else {
      return 'other'
    }
  }

  // OS detection from user agent
  private detectOS(userAgent: string): string {
    if (!userAgent) return 'unknown'
    
    const ua = userAgent.toLowerCase()
    
    if (ua.includes('windows')) {
      return 'windows'
    } else if (ua.includes('mac os') || ua.includes('macos')) {
      return 'macos'
    } else if (ua.includes('linux')) {
      return 'linux'
    } else if (ua.includes('android')) {
      return 'android'
    } else if (ua.includes('ios') || ua.includes('iphone') || ua.includes('ipad')) {
      return 'ios'
    } else {
      return 'other'
    }
  }

  // Utility methods
  private getFieldIdentifier(field: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, index: number): string {
    return field.name ||
           field.id ||
           field.getAttribute('data-name') ||
           field.getAttribute('aria-label') ||
           (field as HTMLInputElement).placeholder ||
           `field-${index}`
  }

  private getFieldType(field: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string {
    if (field.tagName.toLowerCase() === 'select') return 'select'
    if (field.tagName.toLowerCase() === 'textarea') return 'textarea'
    return (field as HTMLInputElement).type || 'text'
  }

  private getFieldValue(field: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string {
    if (field.type === 'checkbox') {
      return (field as HTMLInputElement).checked ? 'checked' : 'unchecked'
    }
    if (field.type === 'radio') {
      return (field as HTMLInputElement).checked ? field.value : ''
    }
    return field.value.trim()
  }

  private getFormId(field: HTMLElement): string | undefined {
    const form = field.closest('form')
    return form?.id || form?.getAttribute('data-form-id') || undefined
  }

  // Public API methods
  public getSessionId(): string {
    // Always check for session changes when getting session ID
    this.checkAndHandleSessionChange()
    return this.sessionManager.getSessionId()
  }

  public getFormData(): Record<string, any> {
    return this.extractFormData()
  }

  public getLeadData(): LeadData {
    return this.extractLeadData()
  }

  public getCompletionPercentage(): number {
    return this.calculateCompletionPercentage()
  }

  public getPendingInteractionCount(): number {
    return this.eventBatcher.getPendingCount(this.sessionManager.getSessionId())
  }

  public async flushPendingInteractions(): Promise<void> {
    await this.eventBatcher.flushAll()
  }

  // REMOVED: forceLeadProcessing was causing sessions to be marked as complete
  // This was creating false credit_decision webhooks on page refresh
  // If you need to process a lead, use the internal methods with proper checks
  
  // Force a complete state reset (useful for debugging)
  public forceStateReset(): void {
    if (this.config.debug) {
      console.log('🆘 Force resetting all tracker state')
    }
    this.resetAllProcessingState()
  }

  public getSessionStats() {
    return {
      sessionId: this.sessionManager.getSessionId(),
      duration: this.sessionManager.getSessionDuration(),
      eventsCount: this.fieldData.length,
      currentStep: this.stepNumber,
      completionPercentage: this.getCompletionPercentage(),
      pendingInteractions: this.getPendingInteractionCount(),
      isOnline: this.isOnline,
      supabaseInitialized: this.supabaseInitialized,
      hasInitialWebhook: this.hasInitialWebhook,
      hasPartialCreatedWebhook: this.hasPartialCreatedWebhook,
      isProcessing: this.isProcessing,
      formSubmitted: this.formSubmitted
    }
  }

  // Private properties for lead processing state
  private lastSentData?: Record<string, any>
  private lastSentFieldCount?: number

  // Loan amount + home-ownership snapshotted at credit-pull time, forwarded
  // into the CD additional-info (scenarios 5/7) dcapi submit. See
  // captureCarryForwardSnapshot / getAdditionalInfoCarryForward.
  private carryForwardSnapshot: Record<string, unknown> = {}

  /**
   * Trigger component renderer on form completion
   * This method is called after processLead(true) to potentially render
   * personalized components based on credit decision results.
   */
  private async triggerComponentRenderer(isFormSubmission: boolean): Promise<void> {
    console.log('🎯 triggerComponentRenderer called, isFormSubmission:', isFormSubmission)

    // Hide the form when submitting
    if (isFormSubmission) {
      console.log('🙈 Hiding form on submission')
      this.hideFormOnSubmit()
    }

    // Only trigger if there are registered handlers
    if (!componentRendererRegistry.hasHandlers()) {
      console.warn('⚠️ No component renderer handlers registered!')
      return
    }

    console.log('✅ Component renderer has handlers, proceeding...')

    try {
      // Extract lead data for enhanced conversions
      const leadData = this.extractLeadData()

      // Snapshot the loan amount + home-ownership the applicant provided BEFORE
      // the credit pull. The CD additional-info form (scenarios 5/7) forwards
      // these into its dcapi submit so the user isn't asked again. Captured here
      // because this is the moment those fields are known-populated.
      this.captureCarryForwardSnapshot(leadData)

      const formCompletionData: FormCompletionData = {
        sessionId: this.sessionManager.getSessionId(),
        submissionTime: Date.now(),
        stepNumber: this.stepNumber,
        isFormSubmission,
        debugMode: this.config.debug,
        formData: {
          first_name: leadData.first_name,
          last_name: leadData.last_name,
          email: leadData.email,
          phone: leadData.phone,
          address_street: leadData.address_street,
          address_city: leadData.address_city,
          address_state: leadData.address_state,
          address_zip: leadData.address_zip
        }
      }

      console.log('🎯 Triggering component renderer with data:', formCompletionData)

      const responses = await componentRendererRegistry.triggerFormCompletion(formCompletionData)

      console.log(`🎯 Component renderer responses:`, responses)

      const successCount = responses.filter(r => r.success).length
      const failCount = responses.filter(r => !r.success).length
      console.log(`🎯 Component renderer completed: ${successCount} success, ${failCount} failed`)

    } catch (error) {
      // Don't let component renderer errors break lead processing
      console.error('🎯 Component renderer trigger failed:', error)
    }
  }

  /**
   * Resolve the loan amount + home-ownership to forward into the CD
   * additional-info submit. Prefers the snapshot captured at credit-pull time
   * (the canonical "before credit decision" values); falls back to a live read
   * of the lead form if no snapshot was captured. Returns only the keys it can
   * resolve, so absent values are simply omitted from the dcapi payload.
   */
  private getAdditionalInfoCarryForward(): Record<string, unknown> {
    if (Object.keys(this.carryForwardSnapshot).length > 0) {
      return this.carryForwardSnapshot
    }
    try {
      return this.buildCarryForwardFields(this.extractLeadData())
    } catch {
      return {}
    }
  }

  /** Store the carry-forward fields if the lead data yields any. */
  private captureCarryForwardSnapshot(leadData: LeadData): void {
    const fields = this.buildCarryForwardFields(leadData)
    if (Object.keys(fields).length > 0) {
      this.carryForwardSnapshot = fields
      if (this.config.debug) {
        console.log('🧾 Captured additional-info carry-forward fields:', fields)
      }
    }
  }

  /**
   * Map lead data → the fields SGN expects on the additional-info submit:
   *   - loan_amount: integer dollars, 100–35000 — ALWAYS sent (defaults to
   *     DEFAULT_LOAN_AMOUNT when the lead carries no usable amount)
   *   - home_owner:  "own" | "rent" — omitted when it can't be derived
   */
  private buildCarryForwardFields(leadData: LeadData): Record<string, unknown> {
    const out: Record<string, unknown> = {}

    // Always present — normalizeLoanAmount falls back to the default.
    out.loan_amount = this.normalizeLoanAmount(leadData.loan_amount)

    const homeOwner = this.normalizeHomeOwner(leadData.housing_status)
    if (homeOwner) out.home_owner = homeOwner

    return out
  }

  /**
   * Coerce a captured loan amount to an integer in SGN's accepted 100–35000
   * range. Accepts a number (from extractLeadData) or a formatted string
   * ("$15,000"). Out-of-range-high values are clamped to 35000. When the amount
   * is absent / unparseable / below the 100 minimum, fall back to
   * DEFAULT_LOAN_AMOUNT (1000) so the additional-info submit ALWAYS carries a
   * valid amount — leads normally land on creditdirectloans.com and pass it via
   * `?loan_amount=` (e.g. /application-form?loan_amount=7500), but if it's
   * missing we must not send nothing (the backend would fall back to amount=0,
   * below SGN's minimum).
   */
  private static readonly DEFAULT_LOAN_AMOUNT = 1000

  private normalizeLoanAmount(value: unknown): number {
    const fallback = UnifiedLeadTracker.DEFAULT_LOAN_AMOUNT
    if (value === undefined || value === null || value === '') return fallback
    const raw =
      typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.]/g, ''))
    if (!Number.isFinite(raw)) return fallback
    const rounded = Math.round(raw)
    if (rounded < 100) return fallback
    return Math.min(rounded, 35000)
  }

  /**
   * Map the lead form's housing status to SGN's home_owner enum
   * ("own" | "rent"). The CD `housing` select offers: Rent / Own /
   * Live With Parents / Other (+ a "Select…" placeholder). SGN only accepts
   * own/rent, so home_owner is effectively "do they own the home?":
   *   - Own (or a mortgage/buying value) → "own"
   *   - Rent / Live With Parents / Other → "rent" (non-owner)
   *   - empty / unselected placeholder    → undefined (omitted from payload)
   */
  private normalizeHomeOwner(value: unknown): 'own' | 'rent' | undefined {
    if (typeof value !== 'string') return undefined
    const v = value.trim().toLowerCase()
    if (!v || v.startsWith('select')) return undefined
    if (v.includes('own') || v.includes('mortgage') || v.includes('buy')) return 'own'
    return 'rent'
  }

  /**
   * Hide the form when it's submitted to make room for outcome components
   */
  private hideFormOnSubmit(): void {
    // Hide the form element itself
    const forms = document.querySelectorAll('form[data-form="multistep"]')
    forms.forEach(form => {
      const htmlForm = form as HTMLElement
      htmlForm.style.display = 'none'
    })

    // Also hide the parent container that wraps the form
    const formContainer = document.querySelector('#accs-apply-form, .loan-form-block')
    if (formContainer) {
      (formContainer as HTMLElement).style.display = 'none'
    }

    // CRITICAL: DO NOT hide the .section parent!
    // The outcome components live in the same .section as the form
    // Hiding .section would make outcome components invisible even when shown
    // Only hide the form elements themselves (done above)

    if (this.config.debug) {
      console.log('🙈 Hidden form and form containers (but not parent section)')
    }
  }

  /**
   * Check for saved completion state (24-hour persistence)
   * If a saved state exists, restore the outcome component instead of showing the form
   *
   * @returns CompletionState if saved state exists and is valid, null otherwise
   */
  private async checkForSavedCompletionState(): Promise<CompletionState | null> {
    console.log('🔍 [RESTORATION] Starting checkForSavedCompletionState...')

    try {
      // Get the component renderer instance
      console.log('🔍 [RESTORATION] Getting component renderer...')
      const renderer = getComponentRenderer()

      if (!renderer) {
        console.error('❌ [RESTORATION] Component renderer not initialized yet - cannot check for saved state')
        return null
      }
      console.log('✅ [RESTORATION] Component renderer found')

      // Check for saved state
      console.log('🔍 [RESTORATION] Checking for saved state in localStorage...')
      const savedState = renderer.checkForSavedState()

      if (!savedState) {
        console.log('ℹ️ [RESTORATION] No saved completion state found in localStorage')
        return null
      }

      console.log('✅ [RESTORATION] Saved completion state found:', {
        sessionId: savedState.sessionId,
        scenario: savedState.scenario,
        timestamp: new Date(savedState.timestamp).toISOString(),
        expiresAt: new Date(savedState.expiresAt).toISOString(),
        hasComponentActions: !!savedState.componentActions,
        componentActionsToShow: savedState.componentActions?.show,
        componentActionsToHide: savedState.componentActions?.hide
      })

      // Hide the form before restoring component
      console.log('🔍 [RESTORATION] Hiding form before restoration...')
      this.hideFormOnSubmit()
      console.log('✅ [RESTORATION] Form hidden')

      // Restore the saved component state
      console.log('🔍 [RESTORATION] Calling restoreSavedState...')
      const result = await renderer.restoreSavedState(savedState)
      console.log('✅ [RESTORATION] restoreSavedState returned:', result)

      if (result.success) {
        console.log('✅ [RESTORATION] Component state restored successfully!', {
          scenarioCode: result.scenarioCode,
          componentsShown: result.componentsShown,
          processingTime: result.processingTime
        })
        return savedState
      } else {
        console.error('❌ [RESTORATION] Failed to restore saved state:', result.error)
        console.log('🔄 [RESTORATION] Showing form back after failure...')
        // CRITICAL: Show the form back if restoration fails
        this.showFormAfterFailedRestore()
        // Clear the saved state so we don't keep trying
        console.log('🗑️ [RESTORATION] Clearing corrupted saved state...')
        renderer.clearCompletionState()
        return null
      }

    } catch (error) {
      console.error('❌ [RESTORATION] Error checking for saved completion state:', error)
      console.error('❌ [RESTORATION] Stack trace:', error)
      console.log('🔄 [RESTORATION] Showing form back after error...')
      // CRITICAL: Show the form back if error occurs
      this.showFormAfterFailedRestore()
      return null
    }
  }

  /**
   * Show the form back after failed restoration
   * This prevents blank page if restoration fails
   */
  private showFormAfterFailedRestore(): void {
    try {
      // Show the form itself
      const forms = document.querySelectorAll('form[data-form="multistep"]')
      forms.forEach(form => {
        const htmlForm = form as HTMLElement
        htmlForm.style.display = ''
      })

      // Show the form container
      const formContainer = document.querySelector('#accs-apply-form, .loan-form-block')
      if (formContainer) {
        (formContainer as HTMLElement).style.display = ''
      }

      // Note: We don't hide/show .section anymore since outcome components live there too

      if (this.config.debug) {
        console.log('🔄 Restored form visibility after failed component restoration')
      }
    } catch (error) {
      console.error('❌ Failed to restore form visibility:', error)
    }
  }
}

// Export the unified system
export default UnifiedLeadTracker

// Auto-initialize for browser environments
if (typeof window !== 'undefined') {
  // CRITICAL: Hide all outcome components IMMEDIATELY on page load
  // This prevents flickering of components before credit decision is received
  hideAllOutcomeComponents()

  // Opt the CD additional-info outcome host out of the global format-engine BEFORE
  // it scans. The form-controller would set this later, but the format-engine runs
  // during tracker init and would have already registered raw-digit fields as
  // phone/currency/SSN — wiping bank account / routing number values as they format.
  const additionalInfoHost = document.querySelector('[data-component="cd-outcome-additional-information"]')
  if (additionalInfoHost) {
    additionalInfoHost.setAttribute('data-no-format', 'true')
  }

  // CRITICAL: Prevent double initialization if script is loaded multiple times
  // This was causing duplicate sessions and leads on page load
  if ((window as any).LeadTracker) {
    console.warn('⚠️ Lead Tracker already initialized - skipping duplicate initialization')
    console.warn('   Existing tracker:', (window as any).LeadTracker)
    console.warn('   This prevents duplicate sessions/leads from multiple script loads')
  } else {
    // CRITICAL: Initialize component system FIRST (before tracker init)
    // This ensures component renderer is available when checking for saved state
    const apiHeaders = {
      'x-auth': 'WSZjhgA3kzCDBScR5VahyPKLSmJHe4xMDXvD2SZYhQf37Md938WzkN66qBHcPB7BwdrqMrcJBTvem2UB2GtnTCde6gAc87VDvpPWUTDfv2Ux98F78mtx2Xs6z3N4SwKV'
    }

    // Initialize component rendering system FIRST (synchronously)
    initializeComponentSystem({
      debugMode: config.debug,
      apiHeaders: apiHeaders
    }).then(result => {
      if (result.success) {
        console.log('🎯 Component Rendering System initialized with API auth')
      } else {
        console.warn('⚠️ Component Rendering System failed to initialize:', result.error)
      }

      // NOW initialize the unified lead tracker (after component system is ready)
      const unifiedTracker = new UnifiedLeadTracker()

      // CRITICAL: Always check for session changes on page load
      unifiedTracker.init()

      // Initialize the field formatting module (independent system)
      try {
        console.log('🔍 About to call initializeFormatter from unified lead tracker')
        console.log('🔍 initializeFormatter function:', typeof initializeFormatter)

        initializeFormatter({
          debug: config.debug,
          autoScan: true,
          gracefulDegradation: true
        })
        console.log('✨ Field Formatting Module initialized')
      } catch (error) {
        console.warn('⚠️ Field Formatting Module failed to initialize:', error)
      }

      // Initialize address autocomplete system
      try {
        initAddressAutocomplete()
        console.log('🏠 Address Autocomplete Module initialized')
      } catch (error) {
        console.warn('⚠️ Address Autocomplete Module failed to initialize:', error)
      }

      // Make globally accessible for debugging and external access
      ;(window as any).UnifiedLeadTracker = UnifiedLeadTracker
      ;(window as any).LeadTracker = unifiedTracker
      ;(window as any).ProDebtTracker = unifiedTracker

      console.log('🎯 Unified Lead Tracker System Ready!')
    }).catch(error => {
      console.error('❌ Failed to initialize component system:', error)
    })
  }
}